import React, { useCallback, useEffect, useState, useRef } from 'react';

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import Login from '../Login';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { useUI } from '../UIContext';
import { doc, getDoc, getDocs, updateDoc, collection, deleteDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../firebase';
import AbcNotation from '../components/AbcNotation';
import FullscreenViewer from '../components/FullscreenViewer';
import { useActiveSession } from '../ActiveSessionContext';
import { sanitizeAbcBody } from '../components/sanitizeAbcBody';
import { loadTuneHybrid } from '../utils/loadTuneHybrid';
import dexie from '../offlineDB';
import TuneRecorder from '../components/TuneRecorder';
import { deleteObject, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import abcjs from 'abcjs';
import { flattenAbcForPlayback } from '../utils/flattenAbcForPlayback';
import { getUserProfile, sendTuneToUser } from '../utils/socialFirestore';
import { useUserPrefs } from '../UserPrefsProvider';
import NotesEditor from '../components/NotesEditor';
import { setDirtyFlag } from '../safeNavigate';
import { normalizeKey, normalizeType, getNormalizedGenreOptions, keyRoot } from '../utils/normalize';
import KeyInput from '../components/KeyInput';
import { getModeLabels, getModeLabelsForEntity, resolveModeForEntity } from '../utils/modeLabels';
import ActionConfirmModal from '../components/ActionConfirmModal';
import SendExportModal from '../components/SendExportModal';
import { addPendingWrite } from '../utils/flushPendingWrites';
import AudioPlayerWithSpeed from '../components/AudioPlayerWithSpeed';
import AppSelect from '../components/AppSelect';
import AbcImportPreview from '../components/AbcImportPreview';
import { cleanAndFillAbc } from '../utils/cleanAndFillAbc';
import InlineEditableTitle from '../components/InlineEditableTitle';
import DetailTagEditor from '../components/DetailTagEditor';
import FullscreenNotesEditorOverlay from '../components/FullscreenNotesEditorOverlay';
import { ATTACHMENT_ACCEPT_ATTR, validateAttachmentFile } from '../utils/attachments';
import PreviewModePill from '../components/PreviewModePill';
import DetailTimestampsFooter from '../components/DetailTimestampsFooter';
import useInternalLinkTargets from '../hooks/useInternalLinkTargets';
import InternalBacklinksPanel from '../components/InternalBacklinksPanel';
import {
  buildInternalLinkHtml,
  findInboundInternalLinks,
  normalizeInternalLinkMarkup,
  removeInternalLinksToTargetFromHtml,
  toInternalLinkTargetOptions,
} from '../utils/internalNoteLinks';
import CreateLinkedNoteModal from '../components/CreateLinkedNoteModal';

const formatShortUsDate = (value) => {
  const date = new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit'
  });
};

const generateClientId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  // RFC 4122 v4 UUID bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
};


export default function TuneDetailPage({ tunes }) {
  const { user } = useAuth();
  const { textStyle } = useUI();
  const { labels, prefs } = useUserPrefs();
  const { id } = useParams();
  const rawNavigate = useNavigate();
  const location = useLocation();
  const [loadedTune, setLoadedTune] = useState(null);
  const [loading, setLoading] = useState(false);
  const { activeSession } = useActiveSession();

  const fallbackTune = tunes?.find(t => t.id === id);
  const locationState = useRef(location.state || {}); // Persist location.state across re-renders
  const [validSessions, setValidSessions] = useState([]);
  const [relatedSets, setRelatedSets] = useState([]);
  const [showSendPicker, setShowSendPicker] = useState(false);
  const [showAddLinkField, setShowAddLinkField] = useState(false);
  const [pendingNewLink, setPendingNewLink] = useState('');
  const [confirmDeleteTune, setConfirmDeleteTune] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [pendingModeChange, setPendingModeChange] = useState(null);
  const [pendingTagSearchTag, setPendingTagSearchTag] = useState(null);
  const [pendingAttachmentDelete, setPendingAttachmentDelete] = useState(null);
  const [forkModalOpen, setForkModalOpen] = useState(false);
  const [forkTitleCandidate, setForkTitleCandidate] = useState('');
  const [forkBusy, setForkBusy] = useState(false);
  const [recordingToTransfer, setRecordingToTransfer] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferSets, setTransferSets] = useState([]);
  const [transferSetId, setTransferSetId] = useState('');
  const [transferMode, setTransferMode] = useState('copy');
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferNotice, setTransferNotice] = useState('');
  const [showAddToSetPicker, setShowAddToSetPicker] = useState(false);
  const [addToSetQuery, setAddToSetQuery] = useState('');
  const [addToSetTargets, setAddToSetTargets] = useState([]);
  const [addToSetLoading, setAddToSetLoading] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editNotesValue, setEditNotesValue] = useState('');
  const notesDraftRef = useRef('');
  const [noteMetaExpanded, setNoteMetaExpanded] = useState(false);
  const [noteAttachmentsExpanded, setNoteAttachmentsExpanded] = useState(false);
  const [noteInfoExpanded, setNoteInfoExpanded] = useState(false);
  const [showRecorder, setShowRecorder] = useState(false);
  const [fullscreenNotes, setFullscreenNotes] = useState(false);
  const [fullscreenNotesDistractionFree, setFullscreenNotesDistractionFree] = useState(false);
  const [notesUiMode, setNotesUiMode] = useState('view'); // 'view' | 'edit' | 'focus' | 'zen'
  const notesAutosaveTimerRef = useRef(null);
  const appMode = prefs?.appMode || 'tunes';
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [optionTunes, setOptionTunes] = useState([]);
  const notesEditorRef = useRef(null);
  const notesEditCardRef = useRef(null);
  const lastViewedStampRef = useRef('');
  const detailFileInputRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const recordingBlobUrlMapRef = useRef(new Map());
  const rawAbcTextareaRef = useRef(null);
  const [isCompactActions, setIsCompactActions] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 560 : false
  ));
  const notesShortcutModLabel = (() => {
    if (typeof navigator === 'undefined') return 'Ctrl';
    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
    return /Mac|iPhone|iPad|iPod/i.test(platform) ? 'Cmd' : 'Ctrl';
  })();
  const { inputStyle } = useUI();
  const normalizeNoteContent = (value) => {
    const raw = String(value || '');
    if (!raw.trim()) return '';
    if (raw.trim().startsWith('<')) return raw.trim();
    return raw
      .split('\n')
      .map((line) => `<p>${line || '<br>'}</p>`)
      .join('');
  };
  const autoSizeRawAbcTextarea = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(220, el.scrollHeight)}px`;
  };

  // Guard all navigation: warn if notes are being edited
  const navigate = (...args) => {
    const tuneModeNotesChanged =
      !isNotesMode &&
      normalizeNoteContent(tuneLongDraft?.notes || '') !== normalizeNoteContent(tune?.notes || '');
    const tuneModeAbcChanged =
      !isNotesMode &&
      String(tuneLongDraft?.abc || '') !== String(tune?.abc || '');
    (async () => {
      try {
        if (isEditingNotes) {
          if (notesAutosaveTimerRef.current) {
            clearTimeout(notesAutosaveTimerRef.current);
            notesAutosaveTimerRef.current = null;
          }
          await flushNotesAutosaveNow();
          setIsEditingNotes(false);
          setFullscreenNotes(false);
        }

        if (tuneModeNotesChanged || tuneModeAbcChanged) {
          await persistTuneUpdates({
            ...(tuneModeNotesChanged ? { notes: tuneLongDraft?.notes || '' } : {}),
            ...(tuneModeAbcChanged ? { abc: tuneLongDraft?.abc || '' } : {}),
          });
        }
        rawNavigate(...args);
      } catch (err) {
        console.error('Failed to persist changes before navigation:', err);
        alert('Could not save changes yet. Please try again.');
      }
    })();
  };

  const navigateToTagSearch = (tag) => {
    let hasFilters = false;
    try {
      const stored = JSON.parse(localStorage.getItem('activeSearchFilters') || '{}');
      const isActive = (val) => val !== null && val !== undefined && val !== '' && val !== '—';
      hasFilters =
        isActive(stored.searchQuery) ||
        isActive(stored.filterKey) ||
        isActive(stored.filterType) ||
        isActive(stored.filterGenre) ||
        isActive(stored.filterStatus) ||
        isActive(stored.filterRating) ||
        isActive(stored.filterRatingLabel) ||
        (Array.isArray(stored.selectedTags) && stored.selectedTags.length > 0);
    } catch (e) {
      console.warn('Could not read stored filters:', e);
    }

    if (!hasFilters) {
      const params = new URLSearchParams();
      params.set('tag', tag);
      navigate(`/search?${params.toString()}`);
      return;
    }

    setPendingTagSearchTag(tag);
  };


function AudioPlayerWithSpeedLegacy({ src }) {
  const [rate, setRate] = useState(1.0);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  }, [rate]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
      {error ? (
        <div style={{ color: 'red', fontSize: '0.85rem' }}>
          Audio error: {error}
        </div>
      ) : (
      <audio
        ref={audioRef}
        controls
        src={src}

        onError={(e) => {
          const code = e.target?.error?.code;
          const codes = { 1: 'aborted', 2: 'network', 3: 'decode', 4: 'format not supported' };
          const msg = codes[code] || `unknown (code ${code})`;
          console.error('Audio playback error:', msg, 'src:', src);
          setError(msg);
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) {
            audioRef.current.playbackRate = rate;
          }
        }}
      />
      )}
      <label style={{ fontSize: '0.9rem' }}>
        Speed:
        <select
          value={rate}
          onChange={(e) => setRate(parseFloat(e.target.value))}
          style={{ marginLeft: '6px' }}
        >
          {[0.5, 0.75, 1.0, 1.25, 1.5].map((r) => (
            <option key={r} value={r}>
              {r}×
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function AbcMidiPlayer({ abc }) {
  const divRef = useRef(null);
  const synthRef = useRef(null);
  const [speed, setSpeed] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);

  const BASE_TEMPO = 140;

  const stop = () => {
    if (synthRef.current) {
      synthRef.current.stop();
      synthRef.current = null;
    }
    setIsPlaying(false);
  };

  const injectTempo = (abc, scaledTempo) => {
    const lines = abc.split('\n');
    const updated = [];
    let inserted = false;

    for (const line of lines) {
      if (line.startsWith('Q:')) {
        updated.push(`Q:1/4=${scaledTempo}`);
        inserted = true;
      } else if (line.match(/^[A-Z]:/)) {
        updated.push(line);
      } else if (!inserted && line.trim() !== '') {
        updated.push(`Q:1/4=${scaledTempo}`);
        updated.push(line);
        inserted = true;
      } else {
        updated.push(line);
      }
    }

    if (!inserted) {
      updated.unshift(`Q:1/4=${scaledTempo}`);
    }

    return updated.join('\n');
  };

  const playWithSpeed = async (speedValue) => {
    stop();

    if (!abcjs.synth.supportsAudio()) {
      alert("Audio not supported in this browser.");
      return;
    }

    try {
    const abcFlat = flattenAbcForPlayback(abc);
    const abcWithTempo = injectTempo(abcFlat, Math.round(BASE_TEMPO * speedValue));
      const visualObj = abcjs.renderAbc(divRef.current, abcWithTempo)[0];

      const synth = new abcjs.synth.CreateSynth();
      synthRef.current = synth;

      await synth.init({ visualObj });
      await synth.prime();
      synth.start();
      setIsPlaying(true);
    } catch (err) {
      console.error("ABC synth error:", err);
    }
  };

  const handleSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    if (isPlaying) {
      playWithSpeed(newSpeed);
    }
  };

  const handlePlay = () => {
    if (!isPlaying) {
      playWithSpeed(speed);
    }
  };

  return (
    <div className="detail-abc-controls">
      <button
        className="btn-subtle search-control-btn detail-action-btn detail-abc-control-btn"
        onClick={handlePlay}
        disabled={isPlaying}
      >
        ▶️ Play ABC
      </button>
      <button
        className="btn-subtle search-control-btn detail-action-btn detail-abc-control-btn"
        onClick={stop}
        disabled={!isPlaying}
      >
        ⏹ Stop
      </button>
      <div className="detail-abc-speed-select-wrap">
        <AppSelect
          className="search-core-select"
          value={String(speed)}
          onChange={(value) => handleSpeedChange(parseFloat(value))}
          options={[0.5, 0.75, 1.0, 1.25, 1.5].map((r) => ({ value: String(r), label: `${r}×` }))}
          active={true}
        />
      </div>
      <div ref={divRef} style={{ display: 'none' }} />
    </div>
  );
}










  useEffect(() => {
  locationState.current = location.state || {};
}, [location.state]);

  const tune = loadedTune || locationState.current.tune || fallbackTune;
  const effectiveMode = resolveModeForEntity(appMode, tune?.mode || 'tunes');
  const isNotesMode = effectiveMode === 'notes';
  const { notes: linkableNotes, folders: linkableFolders } = useInternalLinkTargets(user, isNotesMode);
  const internalLinkTargets = React.useMemo(
    () => toInternalLinkTargetOptions({
      notes: linkableNotes,
      folders: linkableFolders,
      exclude: { targetType: 'note', targetId: tune?.id },
    }),
    [linkableNotes, linkableFolders, tune?.id]
  );
  const embedNotesById = React.useMemo(
    () => Object.fromEntries((linkableNotes || []).map((n) => [n.id, n])),
    [linkableNotes]
  );
  const noteBacklinks = React.useMemo(
    () => findInboundInternalLinks({
      targetType: 'note',
      targetId: tune?.id,
      notes: linkableNotes,
      folders: linkableFolders,
    }),
    [tune?.id, linkableNotes, linkableFolders]
  );
  const buildInternalLinkNavState = () => {
    const currentState = (locationState.current && typeof locationState.current === 'object')
      ? locationState.current
      : {};
    const priorTrail = Array.isArray(currentState.internalLinkTrail)
      ? currentState.internalLinkTrail
      : [];
    const normalizedTrail = priorTrail
      .filter((entry) => entry && typeof entry.path === 'string' && entry.path.trim())
      .map((entry) => ({
        path: String(entry.path).trim(),
        title: String(entry.title || 'Note').trim() || 'Note',
      }));
    const currentEntry = {
      path: location.pathname,
      title: String(tune?.title || 'Note').trim() || 'Note',
    };
    const lastEntry = normalizedTrail[normalizedTrail.length - 1] || null;
    const nextTrail = (lastEntry && lastEntry.path === currentEntry.path)
      ? normalizedTrail
      : [...normalizedTrail, currentEntry].slice(-40);

    return {
      internalLinkTrail: nextTrail,
      internalLinkFrom: currentEntry,
    };
  };
  const navigateInternalLink = ({ targetType, targetId }) => {
    if (!targetId) return;
    navigate(targetType === 'folder' ? `/sets/${targetId}` : `/tune/${targetId}`, {
      state: buildInternalLinkNavState(),
    });
  };
  const handleForkNoteRequest = ({ titleCandidate }) => {
    setForkTitleCandidate(String(titleCandidate || '').trim());
    setForkModalOpen(true);
  };
  const handleConfirmForkNote = async (rawTitle, inheritTags = false) => {
    if (!user?.uid || !tune?.id) return;
    const nextTitle = String(rawTitle || '').trim() || `Untitled Note — ${formatShortUsDate(Date.now())}`;
    setForkBusy(true);
    try {
      if (notesAutosaveTimerRef.current) {
        clearTimeout(notesAutosaveTimerRef.current);
        notesAutosaveTimerRef.current = null;
      }
      await flushNotesAutosaveNow();

      const newId = generateClientId();
      const nowIso = new Date().toISOString();
      const inheritedTags = inheritTags && Array.isArray(tune?.customTags)
        ? tune.customTags.map((tag) => String(tag || '').trim()).filter(Boolean)
        : [];
      const newNoteData = {
        title: nextTitle,
        notes: '',
        genre: tune.genre || '',
        type: tune.type || '',
        mode: 'notes',
        key: '',
        status: '',
        composer: '',
        rating: '',
        quickNote: '',
        lyrics: '',
        abc: '',
        link1: '',
        link2: '',
        link3: '',
        imageUrl: '',
        source: '',
        sessions: [],
        recordings: [],
        attachments: [],
        customTags: inheritedTags,
        eventDate: '',
        createdAt: navigator.onLine ? serverTimestamp() : nowIso,
        updatedAt: navigator.onLine ? serverTimestamp() : nowIso,
        lastViewedAt: '',
      };

      if (navigator.onLine) {
        await setDoc(doc(db, 'users', user.uid, 'tunes', newId), newNoteData);
      } else {
        await dexie.tunes.put({ id: newId, ...newNoteData });
        await addPendingWrite({
          id: `${newId}__createLinkedNote`,
          type: 'add',
          collection: 'tunes',
          docId: newId,
          data: { id: newId, ...newNoteData },
        });
      }

      const parentNotesHtml = notesEditorRef.current?.insertInternalLinkTarget({
        id: newId,
        targetType: 'note',
        title: nextTitle,
      });
      const fallbackParentNotesHtml = (() => {
        const linkHtml = buildInternalLinkHtml({
          targetType: 'note',
          targetId: newId,
          title: nextTitle,
        });
        const base = String(editNotesValue || tune?.notes || '');
        return normalizeInternalLinkMarkup(`${base}${base ? ' ' : ''}${linkHtml}&nbsp;`);
      })();
      const nextParentNotesHtml = typeof parentNotesHtml === 'string' ? parentNotesHtml : fallbackParentNotesHtml;
      applyNotesDraft(nextParentNotesHtml);
      await persistTuneUpdates({ notes: nextParentNotesHtml });

      setForkModalOpen(false);
      setForkTitleCandidate('');
      navigate(`/tune/${newId}`, {
        state: buildInternalLinkNavState(),
      });
    } catch (err) {
      console.error('Failed to create linked note:', err);
      alert('Could not create linked note. Please try again.');
    } finally {
      setForkBusy(false);
    }
  };
  const handleCancelForkNote = () => {
    notesEditorRef.current?.clearForkContext?.();
    setForkModalOpen(false);
  };
  const transferTargetLabel = isNotesMode ? 'Folder' : 'Set';
  const transferTargetLabelLower = transferTargetLabel.toLowerCase();
  const collectionLabel = isNotesMode ? 'Folder' : 'Set';
  const collectionLabelLower = collectionLabel.toLowerCase();
  const itemLabels = getModeLabelsForEntity(appMode, tune?.mode || 'tunes');
const [currentTune, setCurrentTune] = useState(tune);

const handleDeleteTune = async () => {
  if (!tune || !user) return;
  // Route can stay on this component instance when navigating /tune/:id -> /tune/:id,
  // so always clear the delete modal state before continuing.
  setConfirmDeleteTune(false);
  const tuneId = tune.id;
  const referringPath = String(internalLinkFrom?.path || '');
  const referringTuneIdMatch = referringPath.match(/^\/tune\/([^/?#]+)/i);
  const referringTuneId = String(referringTuneIdMatch?.[1] || '').trim();

  // If this note was opened from another note, remove backlinks to this note there.
  if (referringTuneId && referringTuneId !== tuneId) {
    let referringNotes = '';
    const referringCached = embedNotesById?.[referringTuneId] || null;
    if (typeof referringCached?.notes === 'string') {
      referringNotes = referringCached.notes;
    } else if (navigator.onLine) {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid, 'tunes', referringTuneId));
        if (snap.exists()) {
          referringNotes = String(snap.data()?.notes || '');
        }
      } catch (err) {
        console.warn('Could not load referring note before delete cleanup:', err);
      }
    } else {
      const offlineReferrer = await dexie.tunes.get(referringTuneId);
      referringNotes = String(offlineReferrer?.notes || '');
    }

    const cleanedReferringNotes = removeInternalLinksToTargetFromHtml({
      html: referringNotes,
      targetType: 'note',
      targetId: tuneId,
    });
    if (cleanedReferringNotes !== referringNotes) {
      const nowIso = new Date().toISOString();
      const parentUpdates = { notes: cleanedReferringNotes, updatedAt: nowIso };
      if (navigator.onLine) {
        await updateDoc(doc(db, 'users', user.uid, 'tunes', referringTuneId), parentUpdates);
      } else {
        await addPendingWrite({
          id: `${referringTuneId}__update__${Date.now()}`,
          type: 'update',
          collection: 'tunes',
          docId: referringTuneId,
          data: parentUpdates,
        });
      }
      const updatedParent = await dexie.tunes.update(referringTuneId, parentUpdates);
      if (!updatedParent && referringTuneId) {
        await dexie.tunes.put({
          id: referringTuneId,
          ...(referringCached || {}),
          ...parentUpdates,
        });
      }
    }
  }

  // Remove this tune from each set's tunes array
  if (navigator.onLine) {
    const setsSnap = await getDocs(collection(db, 'users', user.uid, 'sets'));
    for (const setDoc of setsSnap.docs) {
      const setData = setDoc.data();
      if ((setData.tunes || []).some(t => t.id === tuneId)) {
        await updateDoc(setDoc.ref, { tunes: setData.tunes.filter(t => t.id !== tuneId) });
      }
    }
    await deleteDoc(doc(db, 'users', user.uid, 'tunes', tuneId));
  } else {
    await addPendingWrite({
      id: `${tuneId}__delete`,
      type: 'delete',
      collection: 'tunes',
      docId: tuneId,
      data: {},
    });
  }
  await dexie.tunes.delete(tuneId);
  if (referringPath) {
    navigate(referringPath);
    return;
  }
  navigate(-1);
};

useEffect(() => {
  // Defensive reset for any modal state that should not carry across tune id changes.
  setConfirmDeleteTune(false);
}, [id]);

const handleConfirmDeleteRecording = async () => {
  if (!recordingToDelete || !tune || !user) return;

  const remaining = (tune.recordings || []).filter(r => r.id !== recordingToDelete.id);
  await dexie.tunes.update(tune.id, { recordings: remaining });

  if (recordingToDelete.storagePath) {
    try {
      const refToDelete = storageRef(storage, recordingToDelete.storagePath);
      await deleteObject(refToDelete);
    } catch (err) {
      console.warn("Failed to delete from storage:", err);
    }
  }

  try {
    const ref = doc(db, 'users', user.uid, 'tunes', tune.id);
    const safeForFirebase = remaining.map(r => ({
      id: r.id,
      label: r.label,
      duration: r.duration,
      createdAt: r.createdAt,
      url: r.url || null,
      storagePath: r.storagePath || null
    }));
    await updateDoc(ref, { recordings: safeForFirebase, updatedAt: new Date().toISOString() });
  } catch (err) {
    console.warn("Failed to update Firestore:", err);
  }

  setLoadedTune(prev => ({
    ...prev,
    recordings: remaining
  }));
  setRecordingToDelete(null);
};

const safeRecordingForStorage = (rec) => ({
  id: rec.id || `rec_${Date.now()}`,
  label: rec.label || '',
  duration: rec.duration || null,
  createdAt: rec.createdAt || new Date().toISOString(),
  url: rec.url || null,
  storagePath: rec.storagePath || rec.path || null,
  mimeType: rec.mimeType || null,
  uploadedAt: rec.uploadedAt || null
});

const getStableRecordingAudioUrl = (rec) => {
  if (rec?.url) return rec.url;
  if (!rec?.localBlob) return null;
  const key = rec.id || `${rec.createdAt || ''}:${rec.label || ''}`;
  const existing = recordingBlobUrlMapRef.current.get(key);
  if (existing) return existing;
  const nextUrl = URL.createObjectURL(rec.localBlob);
  recordingBlobUrlMapRef.current.set(key, nextUrl);
  return nextUrl;
};

const normalizeSearchToolUrl = (value = '') =>
  value.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();

const configuredSearchTools = React.useMemo(() => {
  if (Array.isArray(prefs?.searchTools)) {
    return prefs.searchTools
      .map((tool, idx) => ({
        id: tool?.id || `tool-${idx}`,
        url: normalizeSearchToolUrl(tool?.url || ''),
        enabled: tool?.enabled !== false,
      }))
      .filter((tool) => tool.url && tool.enabled);
  }

  const legacyTools = [
    { id: 'folkfriend', url: 'folkfriend.app', enabled: prefs?.showFolkfriend !== false },
    { id: 'tunepal', url: 'tunepal.org', enabled: prefs?.showTunepal !== false },
    { id: 'tunearch', url: 'tunearch.org', enabled: prefs?.showTunearch !== false },
    { id: 'bluegrasslyrics', url: 'bluegrasslyrics.com', enabled: prefs?.showBluegrasslyrics !== false },
    { id: 'thesession', url: 'thesession.org', enabled: prefs?.showThesession !== false },
    ...((Array.isArray(prefs?.customSearchTools) ? prefs.customSearchTools : []).map((tool, idx) => ({
      id: typeof tool === 'string' ? `custom:${normalizeSearchToolUrl(tool) || idx}` : (tool?.id || `custom:${idx}`),
      url: normalizeSearchToolUrl(typeof tool === 'string' ? tool : (tool?.url || '')),
      enabled: typeof tool === 'string' ? true : tool?.enabled !== false
    })))
  ];
  return legacyTools.filter((tool) => tool.url && tool.enabled);
}, [prefs]);

const tunePageSearchToolsDisabled = React.useMemo(() => {
  const raw = prefs?.tunePageSearchToolsDisabled;
  if (!raw || typeof raw !== 'object') return {};
  return Object.entries(raw).reduce((acc, [url, disabled]) => {
    const normalizedUrl = normalizeSearchToolUrl(url);
    if (normalizedUrl) acc[normalizedUrl] = Boolean(disabled);
    return acc;
  }, {});
}, [prefs?.tunePageSearchToolsDisabled]);

const tunePageSearchTools = React.useMemo(
  () => configuredSearchTools.filter((tool) => !tunePageSearchToolsDisabled[tool.url]),
  [configuredSearchTools, tunePageSearchToolsDisabled]
);
const sessionToolEnabled = !isNotesMode && tunePageSearchTools.some((tool) => tool.url === 'thesession.org');
const orderedTunePageSearchTools = React.useMemo(() => {
  const nonSessionTools = tunePageSearchTools.filter((tool) => tool.url !== 'thesession.org');
  const sessionTool = tunePageSearchTools.find((tool) => tool.url === 'thesession.org');
  return sessionTool ? [...nonSessionTools, sessionTool] : nonSessionTools;
}, [tunePageSearchTools]);
const showHints = Boolean(prefs?.showFieldHints);
const tuneImportedFromSession = React.useMemo(() => {
  const linkCandidates = [
    tune?.source,
    tune?.link1,
    tune?.link2,
    tune?.link3,
    ...(Array.isArray(tune?.links) ? tune.links : []),
  ];
  return linkCandidates.some((value) => /thesession\.org/i.test(String(value || '')));
}, [tune?.source, tune?.link1, tune?.link2, tune?.link3, tune?.links]);

const openSearchTool = (toolUrl) => {
  const normalizedUrl = normalizeSearchToolUrl(toolUrl);
  if (!normalizedUrl) return;

  const title = tuneDraft?.title?.trim() || tune?.title?.trim() || '';
  const encodedTitle = encodeURIComponent(title);

  if (normalizedUrl === 'thesession.org') {
    const url = title
      ? `https://thesession.org/search?type=tune&q=${encodedTitle}`
      : 'https://thesession.org/tunes';
    window.open(url, '_blank');
    return;
  }

  if (normalizedUrl === 'tunearch.org' && title) {
    window.open(`https://tunearch.org/wiki/Special:Search?search=${encodedTitle}`, '_blank');
    return;
  }

  if (normalizedUrl === 'bluegrasslyrics.com' && title) {
    window.open(`https://www.bluegrasslyrics.com/?s=${encodedTitle}`, '_blank');
    return;
  }

  window.open(normalizedUrl.startsWith('http') ? normalizedUrl : `https://${normalizedUrl}`, '_blank');
};

const fetchSessionSuggestions = () => {
  const title = (tuneDraft?.title || tune?.title || '').trim();
  setSessionSearchAttempted(true);
  setSessionSearchLoading(true);
  setDismissSessionSuggestions(false);

  if (!title || title.length < 3) {
    setSessionSuggestions([]);
    setSessionSearchLoading(false);
    return;
  }

  fetch(`https://thesession.org/tunes/search?q=${encodeURIComponent(title)}&format=json`)
    .then((res) => res.json())
    .then((json) => {
      const matches = Array.isArray(json?.tunes)
        ? json.tunes.filter((t) => String(t?.name || '').toLowerCase().includes(title.toLowerCase())).slice(0, 8)
        : [];
      setSessionSuggestions(matches);
    })
    .catch((err) => {
      console.error('Suggestion error:', err);
      setSessionSuggestions([]);
    })
    .finally(() => {
      setSessionSearchLoading(false);
    });
};

const importFromSession = async (tuneId) => {
  try {
    const res = await fetch(`https://thesession.org/tunes/${tuneId}?format=json`);
    const data = await res.json();
    const settings = Array.isArray(data?.settings) ? data.settings : [];
    const versions = settings.map((setting, index) => {
      const abcLines = [
        'X:1',
        `T:${data?.name || ''}`,
        `M:${setting?.meter || ''}`,
        `L:${setting?.unit_note_length || ''}`,
        `R:${data?.type || ''}`,
        `Q:${setting?.tempo || ''}`,
        `Z:${data?.transcribed_by || ''}`,
        `S:${data?.source || ''}`,
        `N:${data?.note || ''}`,
        `K:${setting?.key || ''}`,
        setting?.abc || ''
      ];
      return { index, abc: abcLines.join('\n') };
    });
    const firstVersionAbc = versions[0]?.abc || '';
    const cleaned = cleanAndFillAbc({
      rawAbc: firstVersionAbc,
      type: normalizeType(data?.type || ''),
      key: normalizeKey(settings[0]?.key || '')
    });

    setImportedTune({
      title: data?.name || '',
      type: data?.type || '',
      key: settings[0]?.key || '',
      abc: settings[0]?.abc || '',
      versions,
      selectedIndex: 0,
      sourceLink: `https://thesession.org/tunes/${tuneId}`
    });
    setCustomAbcOverride(cleaned);
    setOriginalAbc(cleaned);
    setImportFields({ title: true, type: true, key: true, abc: true });
    setDismissSessionSuggestions(false);
  } catch (err) {
    console.error(err);
    alert('Error importing tune');
  }
};

const handleImportFromPastedSessionId = async () => {
  const input = sessionInput.trim();
  const match = input.match(/(\d+)/);
  if (!match) {
    alert("Couldn't find a tune ID in that input.");
    return;
  }
  await importFromSession(match[1]);
};

const applyImportedSessionFields = async () => {
  if (!importedTune) return;
  const updates = {};
  if (importFields.title) updates.title = importedTune.title || '';
  if (importFields.type) updates.type = normalizeType(importedTune.type || '');
  if (importFields.key) updates.key = normalizeKey(importedTune.key || '');
  if (importFields.abc) updates.abc = customAbcOverride || '';
  if (Object.keys(updates).length === 0) return;

  await persistTuneUpdates(updates);
  setTuneDraft((prev) => ({
    ...prev,
    ...(updates.title !== undefined ? { title: updates.title } : {}),
    ...(updates.type !== undefined ? { type: updates.type } : {}),
    ...(updates.key !== undefined ? { key: updates.key } : {}),
  }));
  setTuneLongDraft((prev) => ({
    ...prev,
    ...(updates.abc !== undefined ? { abc: updates.abc } : {}),
  }));
  setImportedTune(null);
  setSessionSuggestions([]);
  setDismissSessionSuggestions(false);
};

const openTransferRecordingModal = async (rec) => {
  if (!user) return;
  setTransferNotice('');
  setRecordingToTransfer(rec);
  setTransferSetId('__new__');
  setTransferMode('copy');

  try {
    let setsList = [];
    if (navigator.onLine) {
      const snap = await getDocs(collection(db, 'users', user.uid, 'sets'));
      setsList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } else {
      setsList = await dexie.sets.toArray();
    }
    const eligible = setsList.filter(s =>
      !s.isProgram &&
      Array.isArray(s.tunes) &&
      s.tunes.some(entry => entry?.id === tune?.id)
    );
    setTransferSets(eligible);
    if (eligible.length > 0) setTransferSetId(eligible[0].id);
    setShowTransferModal(true);
  } catch (err) {
    console.error('Failed to load sets for transfer:', err);
    setTransferNotice('Could not load sets.');
  }
};

const openAddToSetPicker = async () => {
  if (!user || !tune?.id) return;
  setAddToSetQuery('');
  setAddToSetLoading(true);
  try {
    let sourceSets = [];
    if (navigator.onLine) {
      const snap = await getDocs(collection(db, 'users', user.uid, 'sets'));
      sourceSets = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } else {
      sourceSets = await dexie.sets.toArray();
    }
    const selectedMode = tune?.mode || 'tunes';
    const eligible = sourceSets
      .filter((set) => !set.isProgram)
      .filter((set) => (set.mode || 'tunes') === selectedMode)
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    setAddToSetTargets(eligible);
    setShowAddToSetPicker(true);
  } catch (err) {
    console.error('Failed to load target sets:', err);
    alert('Could not load sets/folders.');
  } finally {
    setAddToSetLoading(false);
  }
};

const handleAddCurrentItemToSet = async (targetSetId) => {
  if (!user || !tune?.id || !targetSetId) return;
  const itemToAdd = {
    id: tune.id,
    title: tune.title || '',
    key: tune.key || '',
    type: tune.type || '',
  };
  try {
    let targetData = null;
    if (navigator.onLine) {
      const snap = await getDoc(doc(db, 'users', user.uid, 'sets', targetSetId));
      if (!snap.exists()) throw new Error('Target set not found');
      targetData = snap.data();
    } else {
      targetData = await dexie.sets.get(targetSetId);
      if (!targetData) throw new Error('Target set not found (offline)');
    }

    const existingTunes = Array.isArray(targetData.tunes) ? targetData.tunes : [];
    const existingIds = new Set(existingTunes.filter((e) => e?.type !== 'set').map((e) => e.id));
    if (existingIds.has(itemToAdd.id)) {
      alert('This item is already in that set/folder.');
      return;
    }

    const nextTunes = [...existingTunes, itemToAdd];
    const updatedAt = new Date().toISOString();
    const updatedSet = { id: targetSetId, ...targetData, tunes: nextTunes, updatedAt };

    if (navigator.onLine) {
      await updateDoc(doc(db, 'users', user.uid, 'sets', targetSetId), { tunes: nextTunes, updatedAt });
      await dexie.sets.put(updatedSet);
    } else {
      await dexie.sets.put(updatedSet);
      await addPendingWrite({
        id: `${targetSetId}__detailAddToSet`,
        type: 'update',
        collection: 'sets',
        docId: targetSetId,
        data: { tunes: nextTunes, updatedAt },
      });
    }

    setRelatedSets((prev) => {
      if ((prev || []).some((entry) => entry?.setId === targetSetId)) return prev;
      return [
        ...(prev || []),
        {
          setId: targetSetId,
          title: targetData?.title || '',
          createdAt: targetData?.createdAt || updatedAt,
        },
      ];
    });
    setShowAddToSetPicker(false);
    setAddToSetQuery('');
  } catch (err) {
    console.error('Failed to add item to set/folder:', err);
    alert('Failed to add to set/folder.');
  }
};

const handleTransferRecording = async () => {
  if (!user || !tune || !recordingToTransfer || !transferSetId) return;
  setTransferBusy(true);
  setTransferNotice('');
  let shouldNavigateToSetId = null;

  try {
    if (!recordingToTransfer.url) {
      setTransferNotice('This recording is still local-only. Wait for sync/upload, then attach to set.');
      setTransferBusy(false);
      return;
    }
    const recForStorage = safeRecordingForStorage(recordingToTransfer);
    let resolvedSetId = transferSetId;
    let targetSet = null;

    if (transferSetId === '__new__') {
      const nowIso = new Date().toISOString();
      const newSetData = {
        title: `${tune.title || 'New'}`,
        notes: '',
        rating: '',
        ratingLabel: '',
        learnedFrom: '',
        tunes: [{
          id: tune.id,
          title: tune.title || '',
          type: tune.type || 'tune',
          key: tune.key || ''
        }],
        recordings: [recForStorage],
        mode: tune.mode || 'tunes',
        createdAt: nowIso,
        updatedAt: nowIso
      };

      if (navigator.onLine) {
        resolvedSetId = crypto.randomUUID();
        await setDoc(doc(db, 'users', user.uid, 'sets', resolvedSetId), { id: resolvedSetId, ...newSetData });
      } else {
        resolvedSetId = crypto.randomUUID();
        await dexie.sets.put({ id: resolvedSetId, ...newSetData });
        await addPendingWrite({
          id: resolvedSetId,
          type: 'add',
          collection: 'sets',
          docId: resolvedSetId,
          data: { id: resolvedSetId, ...newSetData }
        });
      }
      targetSet = { id: resolvedSetId, ...newSetData };
      shouldNavigateToSetId = resolvedSetId;
    } else {
      const setRef = doc(db, 'users', user.uid, 'sets', transferSetId);
      const localSet = await dexie.sets.get(transferSetId);
      const remoteSetSnap = navigator.onLine ? await getDoc(setRef) : null;
      targetSet = localSet || (remoteSetSnap?.exists() ? { id: transferSetId, ...remoteSetSnap.data() } : null);
    }

    if (!targetSet) {
      setTransferNotice('Destination set not found.');
      setTransferBusy(false);
      return;
    }

    if (resolvedSetId !== '__new__' && transferSetId !== '__new__') {
      const existingSetRecordings = Array.isArray(targetSet.recordings) ? targetSet.recordings : [];
      const alreadyAttached = existingSetRecordings.some(r =>
        (r.id && recForStorage.id && r.id === recForStorage.id) ||
        (r.url && recForStorage.url && r.url === recForStorage.url)
      );

      const nextSetRecordings = alreadyAttached
        ? existingSetRecordings
        : [...existingSetRecordings, recForStorage];

      const setRef = doc(db, 'users', user.uid, 'sets', resolvedSetId);
      if (navigator.onLine) {
        await updateDoc(setRef, { recordings: nextSetRecordings, updatedAt: new Date().toISOString() });
      } else {
        await addPendingWrite({
          id: `${resolvedSetId}__recordingTransfer`,
          type: 'update',
          collection: 'sets',
          docId: resolvedSetId,
          data: { recordings: nextSetRecordings, updatedAt: new Date().toISOString() }
        });
      }
      await dexie.sets.update(resolvedSetId, { recordings: nextSetRecordings, updatedAt: new Date().toISOString() });
    }

    if (transferMode === 'move') {
      const remaining = (tune.recordings || []).filter(r =>
        !((r.id && recordingToTransfer.id && r.id === recordingToTransfer.id) ||
          (r.url && recordingToTransfer.url && r.url === recordingToTransfer.url))
      );
      if (navigator.onLine) {
        await updateDoc(doc(db, 'users', user.uid, 'tunes', tune.id), { recordings: remaining, updatedAt: new Date().toISOString() });
      } else {
        await addPendingWrite({
          id: `${tune.id}__recordingMove`,
          type: 'update',
          collection: 'tunes',
          docId: tune.id,
          data: { recordings: remaining, updatedAt: new Date().toISOString() }
        });
      }
      await dexie.tunes.update(tune.id, { recordings: remaining, updatedAt: new Date().toISOString() });
      setLoadedTune(prev => prev ? { ...prev, recordings: remaining } : prev);
      setCurrentTune(prev => prev ? { ...prev, recordings: remaining } : prev);
    }

    const setTitle = targetSet.title || transferTargetLabelLower;
    setTransferNotice(
      transferMode === 'move'
        ? `Recording moved to "${setTitle}".`
        : `Recording attached to "${setTitle}".`
    );
    setShowTransferModal(false);
    setRecordingToTransfer(null);
    if (shouldNavigateToSetId) {
      navigate(`/sets/${shouldNavigateToSetId}`);
    }
  } catch (err) {
    console.error('Failed to transfer recording:', err);
    setTransferNotice('Could not transfer recording.');
  } finally {
    setTransferBusy(false);
  }
};

// Ensure attachment types are usable even from location.state fallback
if (tune?.attachments?.length) {
  tune.attachments = tune.attachments.map(file => ({
    ...file,
    type:
      file.type ||
      (file.name?.endsWith('.pdf') ? 'application/pdf'
        : /\.(jpg|jpeg|png|gif)$/i.test(file.name) ? 'image/jpeg'
        : 'application/octet-stream')
  }));
}


  // Inline notes editing — dirty flag for navigation protection
  const notesDirty = isEditingNotes && editNotesValue !== (tune?.notes || '');
  React.useEffect(() => {
    setDirtyFlag(notesDirty);
    return () => setDirtyFlag(false);
  }, [notesDirty]);

  // Warn on browser refresh/close while editing notes
  React.useEffect(() => {
    if (!notesDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [notesDirty]);

  useEffect(() => {
    setEditTitleValue(String(tune?.title || ''));
  }, [tune?.id, tune?.title]);

  useEffect(() => {
    notesDraftRef.current = String(editNotesValue || '');
  }, [editNotesValue]);

  const applyNotesDraft = useCallback((nextValue) => {
    const normalized = String(nextValue || '');
    notesDraftRef.current = normalized;
    setEditNotesValue(normalized);
  }, []);

  const handleNotesEditorChange = useCallback((nextValue) => {
    applyNotesDraft(nextValue);
  }, [applyNotesDraft]);

  const flushNotesAutosaveNow = async () => {
    const liveEditorHtml = notesEditorRef.current?.getHtml?.();
    const notesValueForSave = typeof liveEditorHtml === 'string'
      ? liveEditorHtml
      : String(notesDraftRef.current || editNotesValue || '');
    const trimmedTitle = (editTitleValue || '').trim();
    const currentNotes = String(tune?.notes || '');
    const currentTitle = String(tune?.title || '');
    const notesChanged = notesValueForSave !== currentNotes;
    const titleChanged = Boolean(trimmedTitle) && trimmedTitle !== currentTitle;
    if (typeof liveEditorHtml === 'string' && liveEditorHtml !== notesDraftRef.current) {
      applyNotesDraft(liveEditorHtml);
    }
    if (!notesChanged && !titleChanged) return;
    await persistTuneUpdates({
      ...(notesChanged ? { notes: notesValueForSave } : {}),
      ...(titleChanged ? { title: trimmedTitle } : {}),
    });
  };

  const handleDoneNotes = async () => {
    try {
      if (notesAutosaveTimerRef.current) {
        clearTimeout(notesAutosaveTimerRef.current);
        notesAutosaveTimerRef.current = null;
      }
      await flushNotesAutosaveNow();
      setIsEditingNotes(false);
      setFullscreenNotes(false);
      setFullscreenNotesDistractionFree(false);
      setNotesUiMode('view');
    } catch (err) {
      console.error('Failed to save notes:', err);
      alert('Failed to save notes. Please try again.');
    }
  };

  const switchNotesMode = async (targetMode = 'view') => {
    if (!isNotesMode) return;
    const target = String(targetMode || 'view');
    if (!['view', 'edit', 'focus', 'zen'].includes(target)) return;
    if (target === notesUiMode) return;

    if (target === 'view') {
      await handleDoneNotes();
      return;
    }

    if (!isEditingNotes) {
      setEditTitleValue(String(tune?.title || ''));
      applyNotesDraft(tune?.notes || '');
      setIsEditingNotes(true);
    }

    if (target === 'edit') {
      setFullscreenNotes(false);
      setFullscreenNotesDistractionFree(false);
      setNotesUiMode('edit');
      return;
    }

    if (target === 'focus') {
      flushNotesAutosaveNow().catch(() => {});
      setFullscreenNotes(true);
      setFullscreenNotesDistractionFree(false);
      setNotesUiMode('focus');
      return;
    }

    flushNotesAutosaveNow().catch(() => {});
    setFullscreenNotes(true);
    setFullscreenNotesDistractionFree(true);
    setNotesUiMode('zen');
  };

  useEffect(() => {
    if (!isNotesMode) return;
    if (!isEditingNotes) {
      if (notesUiMode !== 'view') setNotesUiMode('view');
      return;
    }
    if (fullscreenNotes) {
      const expected = fullscreenNotesDistractionFree ? 'zen' : 'focus';
      if (notesUiMode !== expected) setNotesUiMode(expected);
      return;
    }
    if (notesUiMode !== 'edit') setNotesUiMode('edit');
  }, [isNotesMode, isEditingNotes, fullscreenNotes, fullscreenNotesDistractionFree, notesUiMode]);

  useEffect(() => {
    if (!isNotesMode) return undefined;
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        if (forkModalOpen) return;
        event.preventDefault();
        const nextMode = notesUiMode === 'view'
          ? 'edit'
          : notesUiMode === 'edit'
            ? 'focus'
            : notesUiMode === 'focus'
              ? 'zen'
              : null;
        if (!nextMode) return;
        switchNotesMode(nextMode).catch((err) => {
          console.error('Failed advancing notes mode with shortcut:', err);
        });
        return;
      }
      if (event.key !== 'Escape') return;
      if (forkModalOpen) return;
      if (notesUiMode === 'view') return;
      event.preventDefault();
      if (notesUiMode === 'zen') {
        setFullscreenNotesDistractionFree(false);
        setNotesUiMode('focus');
        return;
      }
      if (notesUiMode === 'focus') {
        setFullscreenNotes(false);
        setFullscreenNotesDistractionFree(false);
        setNotesUiMode('edit');
        return;
      }
      if (notesUiMode === 'edit') {
        handleDoneNotes().catch((err) => {
          console.error('Failed leaving edit mode with Escape:', err);
        });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isNotesMode, notesUiMode, forkModalOpen, switchNotesMode]);

  useEffect(() => {
    if (!isEditingNotes || fullscreenNotes || forkModalOpen) return undefined;
    const handleOutsideTap = (e) => {
      if (forkModalOpen) return;
      if (!notesEditCardRef.current) return;
      if (notesEditCardRef.current.contains(e.target)) return;
      handleDoneNotes();
    };
    document.addEventListener('mousedown', handleOutsideTap);
    document.addEventListener('touchstart', handleOutsideTap, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutsideTap);
      document.removeEventListener('touchstart', handleOutsideTap);
    };
  }, [isEditingNotes, fullscreenNotes, forkModalOpen, editNotesValue, editTitleValue, tune?.notes, tune?.title]);

  useEffect(() => {
    if (!isEditingNotes) return undefined;
    if (notesAutosaveTimerRef.current) {
      clearTimeout(notesAutosaveTimerRef.current);
      notesAutosaveTimerRef.current = null;
    }

    notesAutosaveTimerRef.current = setTimeout(() => {
      flushNotesAutosaveNow().catch((err) => {
        console.error('Notes autosave failed:', err);
      });
    }, 700);

    return () => {
      if (notesAutosaveTimerRef.current) {
        clearTimeout(notesAutosaveTimerRef.current);
        notesAutosaveTimerRef.current = null;
      }
    };
  }, [isEditingNotes, editNotesValue, editTitleValue, tune?.notes, tune?.title]);

  // Persist latest notes draft on unmount so rapid route changes don't drop formatting edits.
  useEffect(() => () => {
    if (!isEditingNotes) return;
    const liveEditorHtml = notesEditorRef.current?.getHtml?.();
    const notesValueForSave = typeof liveEditorHtml === 'string'
      ? liveEditorHtml
      : String(notesDraftRef.current || '');
    const trimmedTitle = (editTitleValue || '').trim();
    const currentNotes = String(tune?.notes || '');
    const currentTitle = String(tune?.title || '');
    const notesChanged = notesValueForSave !== currentNotes;
    const titleChanged = Boolean(trimmedTitle) && trimmedTitle !== currentTitle;
    if (!notesChanged && !titleChanged) return;
    persistTuneUpdates({
      ...(notesChanged ? { notes: notesValueForSave } : {}),
      ...(titleChanged ? { title: trimmedTitle } : {}),
    }).catch((err) => {
      console.error('Failed to persist notes on unmount:', err);
    });
  }, [isEditingNotes, editTitleValue, tune?.notes, tune?.title, tune?.id, user?.uid]);

  const persistTuneUpdates = async (partial) => {
    if (!user?.uid || !tune?.id) {
      throw new Error('Missing user or tune id');
    }
    const nowIso = new Date().toISOString();
    const updates = { ...(partial || {}), updatedAt: nowIso };
    const tuneRef = doc(db, 'users', user.uid, 'tunes', tune.id);

    if (navigator.onLine) {
      await updateDoc(tuneRef, updates);
    } else {
      await addPendingWrite({
        id: `${tune.id}__update__${Date.now()}`,
        type: 'update',
        collection: 'tunes',
        docId: tune.id,
        data: updates,
      });
    }

    const updatedCount = await dexie.tunes.update(tune.id, updates);
    if (!updatedCount) {
      await dexie.tunes.put({ id: tune.id, ...(tune || {}), ...updates });
    }

    setLoadedTune(prev => ({ ...(prev || tune || {}), ...updates }));
    setCurrentTune(prev => ({ ...(prev || tune || {}), ...updates }));
  };

  // Inline field updater for notes mode
  const updateTuneField = async (field, value) => {
    try {
      await persistTuneUpdates({ [field]: value });
    } catch (err) {
      console.error(`Failed to update ${field}:`, err);
    }
  };

  const handleDetailFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !user) return;
    const existing = tune.attachments || [];
    const uploaded = [];
    const rejected = [];
    for (const file of files) {
      const validation = validateAttachmentFile(file);
      if (!validation.ok) {
        rejected.push(validation.reason);
        continue;
      }
      try {
        const fullPath = `recordings/${user.uid}/attachments/${tune.id}/${file.name}`;
        const destRef = storageRef(storage, fullPath);
        await uploadBytes(destRef, file);
        const url = await getDownloadURL(destRef);
        uploaded.push({ name: file.name, type: file.type, url, path: fullPath, uploadedAt: new Date().toISOString() });
      } catch (err) {
        console.error('Attachment upload failed:', file.name, err);
        alert(`Upload failed for "${file.name}": ${err?.code || err?.message || 'Unknown error'}`);
      }
    }
    if (rejected.length > 0) {
      alert(rejected.join('\n'));
    }
    if (uploaded.length > 0) {
      const updated = [...existing, ...uploaded];
      await updateTuneField('attachments', updated);
    }
    if (detailFileInputRef.current) detailFileInputRef.current.value = null;
  };

  // Compute dropdown options from the tune's entity mode, not global app mode.
  const entityMode = tune?.mode || 'tunes';
  const genreOptions = React.useMemo(() => {
    const defaults = getModeLabels(entityMode)?.defaultGenres || [];
    const customGenresRaw = prefs?.customGenres;
    const customCurrentMode = Array.isArray(customGenresRaw)
      ? customGenresRaw
      : (customGenresRaw?.[entityMode] || []);
    const inUse = [...(tunes || []), ...(optionTunes || [])]
      .filter((t) => (t?.mode || 'tunes') === entityMode)
      .map((t) => t?.genre)
      .filter(Boolean);
    return getNormalizedGenreOptions([...defaults, ...customCurrentMode, ...inUse, tune?.genre || '']);
  }, [entityMode, prefs?.customGenres, tunes, optionTunes, tune?.genre]);

  const typeOptions = React.useMemo(() => {
    const defaults = getModeLabels(entityMode)?.defaultTypes || [];
    const customTypesRaw = prefs?.customTypes;
    const customCurrentMode = Array.isArray(customTypesRaw)
      ? customTypesRaw
      : (customTypesRaw?.[entityMode] || []);
    const inUse = [...(tunes || []), ...(optionTunes || [])]
      .filter((t) => (t?.mode || 'tunes') === entityMode)
      .map((t) => t?.type)
      .filter(Boolean);
    const seen = new Map();
    [...defaults, ...customCurrentMode, ...inUse, tune?.type || ''].forEach(t => {
      const n = String(t || '').trim();
      if (!n) return;
      if (!seen.has(n.toLowerCase())) seen.set(n.toLowerCase(), n);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [entityMode, prefs?.customTypes, tunes, optionTunes, tune?.type]);

  const statusOptions = itemLabels?.statusOptions || ['Status (—)', 'ASAP', 'To Do', 'In Progress', 'Done'];
  const ratingOptions = ['Rating (—)', '★', '★★', '★★★', '★★★★', '★★★★★'];

  const tagSuggestions = React.useMemo(() => {
    const byKey = new Map();
    const addTag = (tag) => {
      if (!tag || typeof tag !== 'string') return;
      const trimmed = tag.trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (!byKey.has(key)) byKey.set(key, trimmed);
    };

    (prefs?.customTags || []).forEach(addTag);
    (tunes || []).forEach(t => (t.customTags || []).forEach(addTag));
    (tune?.customTags || []).forEach(addTag);

    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
  }, [prefs?.customTags, tunes, tune?.customTags]);

  const routeState = (location.state && typeof location.state === 'object')
    ? location.state
    : ((locationState.current && typeof locationState.current === 'object') ? locationState.current : {});
  const setTunes = routeState.setTunes || [];
  const setTitle = routeState.setTitle || null;
  const setId = routeState.setId || null;
  const currentIndex = setTunes.findIndex(t => t.id === id);
  const sessionTunes = routeState.sessionTunes || [];
  const sessionTitle = routeState.sessionTitle || null;
  const sessionIndex = sessionTunes.findIndex(t => t.id === id);
  const [fullscreenPdfUrl, setFullscreenPdfUrl] = useState(null);
  const [fullscreenImgUrl, setFullscreenImgUrl] = useState(null);
  const [fullscreenContent, setFullscreenContent] = useState(null); // null | 'lyrics' | 'abc' | 'notes'
  const [isLyricsDetailsOpen, setIsLyricsDetailsOpen] = useState(false);
  const [isAbcDetailsOpen, setIsAbcDetailsOpen] = useState(false);
  const [isRawAbcOpen, setIsRawAbcOpen] = useState(false);
  const [isTuneNotesDetailsOpen, setIsTuneNotesDetailsOpen] = useState(false);
  const [isGeneralInfoOpen, setIsGeneralInfoOpen] = useState(true);
  const [isAttachmentsSectionOpen, setIsAttachmentsSectionOpen] = useState(true);
  const [isSearchToolsOpen, setIsSearchToolsOpen] = useState(false);
  const [sessionInput, setSessionInput] = useState('');
  const [sessionSearchAttempted, setSessionSearchAttempted] = useState(false);
  const [sessionSearchLoading, setSessionSearchLoading] = useState(false);
  const [sessionSuggestions, setSessionSuggestions] = useState([]);
  const [dismissSessionSuggestions, setDismissSessionSuggestions] = useState(false);
  const [importedTune, setImportedTune] = useState(null);
  const [importFields, setImportFields] = useState({ title: true, type: true, key: true, abc: true });
  const [customAbcOverride, setCustomAbcOverride] = useState('');
  const [, setOriginalAbc] = useState('');
  const [tuneDraft, setTuneDraft] = useState({
    title: '',
    genre: '',
    type: '',
    key: '',
    composer: '',
    status: '',
    rating: '',
    quickNote: '',
    link1: '',
    imageUrl: '',
  });
  const keyRawSuggestions = React.useMemo(() => {
    const root = keyRoot((tuneDraft.key || '').trim());
    if (!root) return [];
    return [`${root} major`, `${root} minor`, `${root} dorian`, `${root} mixolydian`];
  }, [tuneDraft.key]);

  const [tuneLongDraft, setTuneLongDraft] = useState({
    notes: '',
    lyrics: '',
    abc: '',
  });

const searchTuneIds = routeState.searchTuneIds || [];
const searchIndex = routeState.currentIndex ?? -1;
const internalLinkTrail = (Array.isArray(routeState.internalLinkTrail)
  ? routeState.internalLinkTrail
  : [])
  .filter((entry) => entry && typeof entry.path === 'string' && entry.path.trim())
  .map((entry) => ({
    path: String(entry.path).trim(),
    title: String(entry.title || 'Note').trim() || 'Note',
  }));
const internalLinkTrailBack = internalLinkTrail[internalLinkTrail.length - 1] || null;
const internalLinkFrom = routeState.internalLinkFrom || internalLinkTrailBack || null;
const navContext =
  setTunes.length > 1 && currentIndex !== -1 ? 'set'
  : sessionTunes.length > 1 && sessionIndex !== -1 ? 'session'
  : searchTuneIds.length > 1 && searchIndex !== -1 ? 'search'
  : null;

const canPrev =
  navContext === 'set' ? currentIndex > 0
  : navContext === 'session' ? sessionIndex > 0
  : navContext === 'search' ? searchIndex > 0
  : false;

const canNext =
  navContext === 'set' ? currentIndex < setTunes.length - 1
  : navContext === 'session' ? sessionIndex < sessionTunes.length - 1
  : navContext === 'search' ? searchIndex < searchTuneIds.length - 1
  : false;

const backTargetLabel =
  internalLinkTrailBack?.title ? internalLinkTrailBack.title
  : internalLinkFrom?.title ? internalLinkFrom.title
  : navContext === 'search' ? 'Search'
  : navContext === 'set' ? 'Set'
  : navContext === 'session' ? 'Sessions'
  : 'Back';
const isViewingFromSetContext = Boolean(setTitle) && navContext === 'set';
const navList =
  navContext === 'set' ? setTunes
  : navContext === 'session' ? sessionTunes
  : [];
const navPosition =
  navContext === 'set' ? currentIndex
  : navContext === 'session' ? sessionIndex
  : -1;
const navContextLabel = navContext === 'set' ? 'Set' : navContext === 'session' ? 'Session' : '';
const navContextName = navContext === 'set' ? (setTitle || '') : navContext === 'session' ? (sessionTitle || '') : '';
const navPositionSummary = navList.length > 1 && navPosition >= 0 ? `${navPosition + 1} of ${navList.length}` : '';
const compactActionStyle = isCompactActions
  ? { fontSize: '0.78rem', padding: '5px 8px', lineHeight: 1.1, whiteSpace: 'nowrap' }
  : { whiteSpace: 'nowrap' };

const noteStatusSelectOptions = [
  { value: '', label: statusOptions[0] || 'Status (—)' },
  ...statusOptions.slice(1).map((s) => ({ value: s, label: s }))
];
const noteRatingSelectOptions = [
  { value: '', label: ratingOptions[0] || 'Rating (—)' },
  ...ratingOptions.slice(1).map((r) => ({ value: r, label: r }))
];
const persistSimpleField = async (field, value) => {
  const currentRaw = tune?.[field] || '';
  const nextRaw = typeof value === 'string' ? value.trim() : (value || '');
  const current = field === 'key' ? normalizeKey(currentRaw) : currentRaw;
  const next = field === 'key' ? normalizeKey(nextRaw) : nextRaw;
  if ((current || '') === (next || '')) return;
  await updateTuneField(field, next);
};
const saveLongField = async (field) => {
  if (isNotesMode) return;
  const next = tuneLongDraft[field] || '';
  const current = tune?.[field] || '';
  if (next === current) return;
  await updateTuneField(field, next);
};
const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value);
};
const isImageAttachment = (file = {}) => {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '');
  const url = String(file?.url || '');
  return (
    type.startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif)(\?.*)?$/i.test(name) ||
    /\.(png|jpe?g|gif|webp|svg|bmp|heic|heif)(\?.*)?$/i.test(url)
  );
};
const isPdfAttachment = (file = {}) => {
  const type = String(file?.type || '').toLowerCase();
  const name = String(file?.name || '');
  const url = String(file?.url || '');
  return (
    type === 'application/pdf' ||
    /\.pdf(\?.*)?$/i.test(name) ||
    /\.pdf(\?.*)?$/i.test(url)
  );
};
const openAttachmentViewer = (file = {}) => {
  const url = String(file?.url || '').trim();
  if (!url) return;
  const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 900px)').matches;
  if (isDesktop) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (isImageAttachment(file)) {
    setFullscreenImgUrl(url);
    return;
  }
  if (isPdfAttachment(file)) {
    setFullscreenPdfUrl(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};
const tuneModeNotesDirty = normalizeNoteContent(tuneLongDraft.notes) !== normalizeNoteContent(tune?.notes || '');
const tuneModeAbcDirty = String(tuneLongDraft.abc || '') !== String(tune?.abc || '');
const tuneModeLongDirty = tuneModeNotesDirty || tuneModeAbcDirty;
const topAttachmentImages = (Array.isArray(tune?.attachments) ? tune.attachments : []).filter((file) => (
  file?.url && isImageAttachment(file)
));
const stripFieldPrefix = (value, prefix) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (!trimmed.toLowerCase().startsWith(prefix.toLowerCase())) return trimmed;
  return trimmed.slice(prefix.length).trimStart();
};
const titleInputStyle = { ...(inputStyle || {}) };
delete titleInputStyle.border;
delete titleInputStyle.borderRadius;
delete titleInputStyle.padding;
delete titleInputStyle.fontSize;
delete titleInputStyle.lineHeight;

React.useEffect(() => {
  if (isNotesMode) return;
  setDirtyFlag(tuneModeLongDirty);
  return () => setDirtyFlag(false);
}, [isNotesMode, tuneModeLongDirty]);

React.useEffect(() => {
  if (isNotesMode || !tuneModeLongDirty) return;
  const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}, [isNotesMode, tuneModeLongDirty]);

const goBack = () => {
  if (internalLinkTrail.length > 0) {
    const previous = internalLinkTrail[internalLinkTrail.length - 1];
    const remainingTrail = internalLinkTrail.slice(0, -1);
    navigate(previous.path, {
      state: {
        internalLinkTrail: remainingTrail,
        internalLinkFrom: remainingTrail[remainingTrail.length - 1] || null,
      },
    });
    return;
  }
  if (internalLinkFrom?.path) {
    navigate(internalLinkFrom.path);
    return;
  }
  if (navContext === 'search') {
    navigate('/search');
    return;
  }
  if (navContext === 'set' && setId) {
    navigate(`/sets/${setId}`, { state: { setId, setTitle, setTunes } });
    return;
  }
  if (navContext === 'session') {
    navigate('/sessions');
    return;
  }
  navigate(-1);
};

const goPrev = () => {
  if (!canPrev) return;
  if (navContext === 'set') {
    const prevItem = setTunes[currentIndex - 1];
    if (prevItem?.type === 'set') {
      navigate(`/sets/${prevItem.id}`, { state: { setId, setTitle, setTunes } });
    } else {
      navigate(`/tune/${prevItem.id}`, { state: { tune: prevItem, setId, setTitle, setTunes } });
    }
    return;
  }
  if (navContext === 'session') {
    navigate(`/tune/${sessionTunes[sessionIndex - 1].id}`, {
      state: { tune: sessionTunes[sessionIndex - 1], sessionTunes, sessionTitle }
    });
    return;
  }
  if (navContext === 'search') {
    navigate(`/tune/${searchTuneIds[searchIndex - 1]}`, {
      state: { searchTuneIds, currentIndex: searchIndex - 1 }
    });
  }
};

const goNext = () => {
  if (!canNext) return;
  if (navContext === 'set') {
    const nextItem = setTunes[currentIndex + 1];
    if (nextItem?.type === 'set') {
      navigate(`/sets/${nextItem.id}`, { state: { setId, setTitle, setTunes } });
    } else {
      navigate(`/tune/${nextItem.id}`, { state: { tune: nextItem, setId, setTitle, setTunes } });
    }
    return;
  }
  if (navContext === 'session') {
    navigate(`/tune/${sessionTunes[sessionIndex + 1].id}`, {
      state: { tune: sessionTunes[sessionIndex + 1], sessionTunes, sessionTitle }
    });
    return;
  }
  if (navContext === 'search') {
    navigate(`/tune/${searchTuneIds[searchIndex + 1]}`, {
      state: { searchTuneIds, currentIndex: searchIndex + 1 }
    });
  }
};

const pageRef = useRef(null);
const wheelAccum = useRef(0);
const wheelTimer = useRef(null);
const wheelLocked = useRef(false);
const shouldIgnoreSwipeNavFromTarget = (target) =>
  Boolean(target && typeof target.closest === 'function' && target.closest('[data-swipe-nav-lock="1"]'));

const handleSwipeNav = (direction) => {
  // direction: 'left' = next, 'right' = prev
  if (setTunes.length > 1 && currentIndex !== -1) {
    if (direction === 'right' && currentIndex > 0) {
      const prevItem = setTunes[currentIndex - 1];
      if (prevItem?.type === 'set') {
        navigate(`/sets/${prevItem.id}`, { state: { setId, setTitle, setTunes } });
      } else {
        navigate(`/tune/${prevItem.id}`, { state: { tune: prevItem, setId, setTitle, setTunes } });
      }
    } else if (direction === 'left' && currentIndex < setTunes.length - 1) {
      const nextItem = setTunes[currentIndex + 1];
      if (nextItem?.type === 'set') {
        navigate(`/sets/${nextItem.id}`, { state: { setId, setTitle, setTunes } });
      } else {
        navigate(`/tune/${nextItem.id}`, { state: { tune: nextItem, setId, setTitle, setTunes } });
      }
    }
  } else if (sessionTunes.length > 1 && sessionIndex !== -1) {
    if (direction === 'right' && sessionIndex > 0) {
      navigate(`/tune/${sessionTunes[sessionIndex - 1].id}`, {
        state: { tune: sessionTunes[sessionIndex - 1], sessionTunes, sessionTitle }
      });
    } else if (direction === 'left' && sessionIndex < sessionTunes.length - 1) {
      navigate(`/tune/${sessionTunes[sessionIndex + 1].id}`, {
        state: { tune: sessionTunes[sessionIndex + 1], sessionTunes, sessionTitle }
      });
    }
  } else if (searchTuneIds.length > 1 && searchIndex !== -1) {
    if (direction === 'right' && searchIndex > 0) {
      navigate(`/tune/${searchTuneIds[searchIndex - 1]}`, {
        state: { searchTuneIds, currentIndex: searchIndex - 1 }
      });
    } else if (direction === 'left' && searchIndex < searchTuneIds.length - 1) {
      navigate(`/tune/${searchTuneIds[searchIndex + 1]}`, {
        state: { searchTuneIds, currentIndex: searchIndex + 1 }
      });
    }
  }
};

// Trackpad two-finger horizontal swipe navigation
useEffect(() => {
  const el = pageRef.current;
  if (!el) return;
  const onWheel = (e) => {
    if (shouldIgnoreSwipeNavFromTarget(e?.target)) return;
    if (Math.abs(e.deltaX) < Math.abs(e.deltaY) * 1.5) return; // mostly vertical scroll
    wheelAccum.current += e.deltaX;
    clearTimeout(wheelTimer.current);
    wheelTimer.current = setTimeout(() => {
      wheelAccum.current = 0;
      wheelLocked.current = false;
    }, 300);
    if (!wheelLocked.current && Math.abs(wheelAccum.current) > 80) {
      wheelLocked.current = true;
      handleSwipeNav(wheelAccum.current > 0 ? 'left' : 'right');
      wheelAccum.current = 0;
    }
  };
  el.addEventListener('wheel', onWheel, { passive: true });
  return () => el.removeEventListener('wheel', onWheel);
});

useEffect(() => {
  const onResize = () => setIsCompactActions(window.innerWidth <= 560);
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
}, []);

useEffect(() => {
  const fetchTune = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await loadTuneHybrid(user, id);
      if (data) setLoadedTune(data);
    } catch (err) {
      console.error('Error loading tune (hybrid):', err);
    } finally {
      setLoading(false);
    }
  };
  fetchTune();
}, [user, id]);

useEffect(() => {
  let cancelled = false;
  const loadOptionTunes = async () => {
    try {
      const localTunes = await dexie.tunes.toArray();
      if (!cancelled) setOptionTunes(Array.isArray(localTunes) ? localTunes : []);
    } catch (err) {
      if (!cancelled) setOptionTunes([]);
    }
  };
  loadOptionTunes();
  return () => {
    cancelled = true;
  };
}, [user?.uid]);

useEffect(() => {
  if (!user || !tune?.id) return;
  const stampKey = `${user.uid}:${tune.id}`;
  if (lastViewedStampRef.current === stampKey) return;
  lastViewedStampRef.current = stampKey;

  const nowIso = new Date().toISOString();
  setLoadedTune(prev => prev ? { ...prev, lastViewedAt: nowIso } : prev);

  (async () => {
    try {
      if (navigator.onLine) {
        await updateDoc(doc(db, 'users', user.uid, 'tunes', tune.id), { lastViewedAt: nowIso });
      }
      await dexie.tunes.update(tune.id, { lastViewedAt: nowIso });
    } catch (err) {
      console.warn('Could not update lastViewedAt:', err);
    }
  })();
}, [user, tune?.id]);

useEffect(() => {
  setCurrentTune(tune);
}, [tune]);

useEffect(() => {
  if (!tune) return;
  setTuneDraft({
    title: tune.title || '',
    genre: tune.genre || '',
    type: tune.type || '',
    key: tune.key || '',
    composer: stripFieldPrefix(tune.composer || '', 'Composer:'),
    status: tune.status || '',
    rating: tune.rating || '',
    quickNote: stripFieldPrefix(tune.quickNote || '', 'Quicknote:'),
    link1: tune.link1 || '',
    imageUrl: tune.imageUrl || '',
  });
  setTuneLongDraft({
    notes: tune.notes || '',
    lyrics: tune.lyrics || '',
    abc: tune.abc || '',
  });
}, [tune?.id, tune?.title, tune?.genre, tune?.type, tune?.key, tune?.composer, tune?.status, tune?.rating, tune?.quickNote, tune?.link1, tune?.imageUrl, tune?.notes, tune?.lyrics, tune?.abc]);

useEffect(() => {
  setIsLyricsDetailsOpen(Boolean((tune?.lyrics || '').trim()));
}, [tune?.id, tune?.lyrics]);

useEffect(() => {
  setIsAbcDetailsOpen(Boolean((tune?.abc || '').trim()));
}, [tune?.id, tune?.abc]);

useEffect(() => {
  setIsRawAbcOpen(false);
}, [tune?.id]);

useEffect(() => {
  if (!isRawAbcOpen) return;
  autoSizeRawAbcTextarea(rawAbcTextareaRef.current);
}, [isRawAbcOpen, tuneLongDraft.abc, tune?.id]);

useEffect(() => {
  if (!isRawAbcOpen) return undefined;
  const handleResize = () => autoSizeRawAbcTextarea(rawAbcTextareaRef.current);
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, [isRawAbcOpen]);

useEffect(() => {
  setIsTuneNotesDetailsOpen(Boolean((tune?.notes || '').trim()));
}, [tune?.id]);

useEffect(() => {
  setIsGeneralInfoOpen(true);
  setIsAttachmentsSectionOpen(true);
  setIsSearchToolsOpen(false);
  setShowAddLinkField(false);
  setPendingNewLink('');
}, [tune?.id]);

useEffect(() => {
  // Revoke blob URLs for recordings no longer present to avoid leaks and churn.
  const activeKeys = new Set(
    (tune?.recordings || []).map((r) => r.id || `${r.createdAt || ''}:${r.label || ''}`)
  );
  for (const [key, url] of recordingBlobUrlMapRef.current.entries()) {
    if (!activeKeys.has(key)) {
      URL.revokeObjectURL(url);
      recordingBlobUrlMapRef.current.delete(key);
    }
  }
}, [tune?.recordings]);

useEffect(() => () => {
  for (const [, url] of recordingBlobUrlMapRef.current.entries()) {
    URL.revokeObjectURL(url);
  }
  recordingBlobUrlMapRef.current.clear();
}, []);


useEffect(() => {
  const filterValidSets = async () => {
    if (!user || !tune?.sets?.length) return;

    const validSets = [];

    for (const s of tune.sets) {
      const id = s.setId;
      if (!id) continue;

      let exists = false;
      if (navigator.onLine) {
        const snap = await getDoc(doc(db, 'users', user.uid, 'sets', id));
        exists = snap.exists();
      } else {
        const local = await dexie.sets.get(id);
        exists = !!local;
      }

      if (exists) validSets.push(s);
    }

    setLoadedTune(prev => ({
      ...prev,
      sets: validSets
    }));
  };

  filterValidSets();
}, [user, tune?.sets]);

useEffect(() => {
  let cancelled = false;
  const loadRelatedSets = async () => {
    if (!tune?.id) {
      if (!cancelled) setRelatedSets([]);
      return;
    }

    const merged = new Map();
    const addSetRef = (raw) => {
      const setId = raw?.setId || raw?.id;
      if (!setId) return;
      const prev = merged.get(setId) || {};
      merged.set(setId, {
        setId,
        title: raw?.title || prev.title || '',
        createdAt: raw?.createdAt || prev.createdAt || '',
      });
    };

    if (Array.isArray(tune.sets)) {
      tune.sets.forEach(addSetRef);
    }

    try {
      const cachedSets = await dexie.sets.toArray();
      cachedSets.forEach((setItem) => {
        const tuneEntries = Array.isArray(setItem?.tunes) ? setItem.tunes : [];
        const tuneIds = Array.isArray(setItem?.tuneIds) ? setItem.tuneIds : [];
        const containsTune =
          tuneIds.includes(tune.id) ||
          tuneEntries.some((entry) => (
            typeof entry === 'string'
              ? entry === tune.id
              : entry?.id === tune.id || entry?.tuneId === tune.id
          ));
        if (containsTune) {
          addSetRef({ setId: setItem.id, title: setItem.title, createdAt: setItem.createdAt });
        }
      });
    } catch (err) {
      console.warn('Could not derive related sets from local cache:', err);
    }

    if (!cancelled) {
      setRelatedSets(Array.from(merged.values()));
    }
  };

  loadRelatedSets();
  return () => {
    cancelled = true;
  };
}, [tune?.id, tune?.sets]);

useEffect(() => {
  if (!user || !tune?.sessions) return;

  const loadSessions = async () => {
    const checks = await Promise.all(
      tune.sessions.map(async (s) => {
        if (!s.sessionId) return null;

        let exists = false;
        if (navigator.onLine) {
          const snap = await getDoc(doc(db, 'users', user.uid, 'sessions', s.sessionId));
          exists = snap.exists();
        } else {
          const local = await dexie.sessions.get(s.sessionId);
          exists = !!local;
        }

        return exists ? s : null;
      })
    );
    setValidSessions(checks.filter(Boolean));
  };

  loadSessions();
}, [user, tune]);





async function handleSendTune(recipientUid, contact) {
  const myProfile = await getUserProfile(user.uid);
  await sendTuneToUser(user.uid, myProfile, recipientUid, tune.id, tune.title);
  // Record sharedWith on the tune
  const tuneRef = doc(db, 'users', user.uid, 'tunes', tune.id);
  const tuneSnap = await getDoc(tuneRef);
  if (tuneSnap.exists()) {
    const existing = tuneSnap.data().sharedWith || [];
    if (!existing.some(s => s.uid === recipientUid)) {
      await updateDoc(tuneRef, {
        sharedWith: [...existing, {
          uid: recipientUid,
          displayName: contact?.displayName || '',
          username: contact?.username || '',
          sentAt: new Date().toISOString()
        }],
        updatedAt: new Date().toISOString()
      });
      setLoadedTune(prev => prev ? {
        ...prev,
        sharedWith: [...(prev.sharedWith || []), { uid: recipientUid, displayName: contact?.displayName || '', username: contact?.username || '' }]
      } : prev);
    }
  }
}

  if (!user) return <PageWrapperAuto><Login /></PageWrapperAuto>;
if (loading) return <PageWrapperAuto><p>Loading tune...</p></PageWrapperAuto>;

if (!tune) {
  return (
    <PageWrapperAuto>
      <p style={{ textAlign: 'center' }}>
        ⚠️ Could not load {itemLabels.item}. You may be offline, not logged in, or the {itemLabels.item} was deleted.
      </p>
    </PageWrapperAuto>
  );
}

  return (
    <PageWrapperAuto>
<div
  ref={pageRef}
  className={`tune-detail-page ${isNotesMode ? 'tune-detail-notes-mode' : 'tune-detail-tunes-mode'}`}
  onTouchStart={e => {
    if (shouldIgnoreSwipeNavFromTarget(e?.target)) {
      touchStartX.current = null;
      touchStartY.current = null;
      return;
    }
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }}
  onTouchEnd={e => {
    if (shouldIgnoreSwipeNavFromTarget(e?.target)) return;
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      handleSwipeNav(dx < 0 ? 'left' : 'right');
    }
  }}
>
      {setTitle && (
  <div style={{ marginTop: '8px', marginBottom: '4px', textAlign: 'center' }}>
    <h4 style={{ ...textStyle, textTransform: 'uppercase', margin: 0 }}>
      Set: {setTitle}{navContext === 'set' && navPositionSummary ? ` • ${navPositionSummary}` : ''}
    </h4>
  </div>
)}

{sessionTitle && (
  <div style={{ marginTop: '8px', marginBottom: '4px', textAlign: 'center' }}>
    <h4 style={{ ...textStyle, textTransform: 'uppercase', margin: 0 }}>
      Session: {sessionTitle}
      {navContext === 'session' && navPositionSummary ? ` • ${navPositionSummary}` : ''}
    </h4>
  </div>
)}

<div
  className="page"
  style={{
    marginTop: '0px',
    paddingTop: '4px',
    width: '100%',
    maxWidth: isNotesMode ? '800px' : '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  }}
>

        <div
          style={{
            padding: 0,
            marginBottom: '2px',
          }}
        >  
          <div className="detail-actions-bar top-detail-actions-bar">
  <div
    className="detail-actions-side detail-actions-left top-detail-actions-side top-detail-actions-left"
    style={{ justifyContent: 'flex-start', flex: '1 1 0' }}
  >
    <button
      className="btn-subtle search-control-btn detail-action-btn top-detail-action-btn"
      onClick={goPrev}
      disabled={!canPrev}
      style={{ ...(compactActionStyle || {}), ...(!canPrev ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
      title="Previous"
    >
      {isCompactActions ? '← Prev' : '← Prev'}
    </button>

    <button
      className="btn-subtle search-control-btn detail-action-btn top-detail-action-btn"
      onClick={goBack}
      title={`Back to ${backTargetLabel}`}
      style={compactActionStyle}
    >
      {isCompactActions ? `← ${backTargetLabel}` : `← ${backTargetLabel}`}
    </button>
  </div>

  <div
    className="detail-actions-side detail-actions-right top-detail-actions-side top-detail-actions-right"
    style={{ justifyContent: 'flex-end', flex: '1 1 0' }}
  >
    {!isViewingFromSetContext && (
      <button
        className="btn-subtle search-control-btn detail-action-btn top-detail-action-btn"
        onClick={openAddToSetPicker}
        title={`Add to ${collectionLabelLower}`}
        style={compactActionStyle}
      >
        {isCompactActions ? `+ ${collectionLabel}` : `+ Add to ${collectionLabel}`}
      </button>
    )}
    <button
      className="btn-subtle search-control-btn detail-action-btn top-detail-action-btn"
      onClick={() => setShowSendPicker(true)}
      title="Send"
      style={compactActionStyle}
    >
      {isCompactActions ? '📤' : '📤 Send / Export'}
    </button>

    <button
      className="btn-subtle search-control-btn detail-action-btn top-detail-action-btn"
      onClick={goNext}
      disabled={!canNext}
      style={{ ...(compactActionStyle || {}), ...(!canNext ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
      title="Next"
    >
      {isCompactActions ? 'Next →' : 'Next →'}
    </button>
  </div>
</div>
{isNotesMode ? (
  /* ===== NOTES MODE: inline editing layout ===== */
  <>
    {/* Title row */}
    <InlineEditableTitle
      isEditing={isEditingTitle}
      value={editTitleValue}
      displayValue={tune.title}
      onChange={setEditTitleValue}
      onStartEditing={() => {
        setEditTitleValue(tune.title || '');
        setIsEditingTitle(true);
      }}
      onCommit={async () => {
        const trimmed = editTitleValue.trim();
        if (!trimmed) {
          setIsEditingTitle(false);
          return;
        }
        await updateTuneField('title', trimmed);
        setIsEditingTitle(false);
      }}
      onCancel={() => {
        setEditTitleValue(tune?.title || '');
        setIsEditingTitle(false);
      }}
      rowClassName="inline-editable-title-row detail-title-row"
      displayClassName="inline-editable-title-display detail-title-display detail-title-shell detail-title-heading"
      inputClassName="search-input inline-editable-title-input detail-title-input detail-title-shell detail-title-heading"
      inputStyle={{ ...inputStyle, boxSizing: 'border-box' }}
    />

    {/* Notes body — hero content */}
    {showHints && (
      <p className="detail-meta-muted" style={{ textAlign: 'center', marginTop: '-2px', marginBottom: '8px' }}>
        Tap the note card to edit. {notesShortcutModLabel}+Enter/Esc cycles modes. Unsaved changes prompt before leaving.
      </p>
    )}
    <div style={{ marginBottom: '0.75rem' }}>
      <div
        ref={notesEditCardRef}
        className="note-card note-card-editing notes-content-surface"
        onPointerDownCapture={(event) => {
          if (fullscreenNotes) return;
          if (isEditingNotes) return;
          if (event?.button !== undefined && event.button !== 0) return;
          const target = event?.target;
          if (target && typeof target.closest === 'function') {
            if (target.closest('.internal-note-link-card-title-link')) return;
            if (target.closest('[data-amn-link="1"]:not(.internal-note-link-inline-card)')) return;
          }
          switchNotesMode('edit').catch((err) => {
            console.error('Failed entering edit mode:', err);
          });
        }}
        title={!isEditingNotes ? `Click to edit (${notesShortcutModLabel}+Enter/Esc cycles modes)` : undefined}
        style={{ minHeight: '3rem', cursor: isEditingNotes ? 'default' : 'text' }}
      >
        {!fullscreenNotes && (isEditingNotes || (tune.notes && String(tune.notes).trim())) ? (
          <NotesEditor
            ref={notesEditorRef}
            value={isEditingNotes ? editNotesValue : (tune.notes || '')}
            onChange={isEditingNotes ? handleNotesEditorChange : () => {}}
            placeholder={isEditingNotes ? 'Write your note...' : 'Tap to add notes...'}
            hideToolbar={!isEditingNotes}
            readOnly={!isEditingNotes}
            onFullscreen={isEditingNotes ? () => {
              switchNotesMode('focus').catch((err) => {
                console.error('Failed entering focus mode:', err);
              });
            } : undefined}
            enableInternalLinks={isNotesMode}
            internalLinkTargets={internalLinkTargets}
            internalLinkNotesById={embedNotesById}
            onInternalLinkNavigate={navigateInternalLink}
            onForkNoteRequested={isEditingNotes && isNotesMode ? handleForkNoteRequest : null}
            showForkButton={Boolean(isEditingNotes && isNotesMode)}
          />
        ) : <span className="note-empty-placeholder">Tap to add notes...</span>}
      </div>
    </div>

    {/* Tags — compact chips below note, above collapsibles */}
    <div className="detail-note-tags-row">
      <DetailTagEditor
        tags={tune.customTags || []}
        onChange={(tags) => updateTuneField('customTags', tags)}
        suggestions={tagSuggestions}
        inputStyle={inputStyle}
        onTagClick={navigateToTagSearch}
      />
      {showHints && (
        <p className="detail-meta-muted" style={{ textAlign: 'center', marginTop: '4px' }}>
          Tags help you filter quickly from Search, Sets, and Sessions.
        </p>
      )}
    </div>

    {/* Hidden file input — always in DOM so ref works */}
    <input
      ref={detailFileInputRef}
      type="file"
      accept={ATTACHMENT_ACCEPT_ATTR}
      multiple
      onChange={handleDetailFileUpload}
      style={{ display: 'none' }}
    />

    {/* Attributes collapsible */}
    {(() => {
      const allLinks = [];
      [tune.link1, tune.link2, tune.link3].forEach(l => { if (l?.trim()) allLinks.push(l.trim()); });
      (tune.links || []).forEach(l => { if (l?.trim() && !allLinks.includes(l.trim())) allLinks.push(l.trim()); });
      const saveLinks = async (updated) => {
        const tuneRef = doc(db, 'users', user.uid, 'tunes', tune.id);
        const nowIso = new Date().toISOString();
        await updateDoc(tuneRef, { link1: updated[0] || '', link2: updated[1] || '', link3: updated[2] || '', links: updated, updatedAt: nowIso });
        setLoadedTune(prev => prev ? { ...prev, link1: updated[0] || '', link2: updated[1] || '', link3: updated[2] || '', links: updated, updatedAt: nowIso } : prev);
      };
      const parts = [tune.genre, tune.type, tune.status, tune.rating].filter(Boolean);
      if (allLinks.length > 0) parts.push(`${allLinks.length} link${allLinks.length > 1 ? 's' : ''}`);
      const attrSummary = parts.length ? parts.join(' · ') : 'Attributes';
      return (
        <div className="detail-note-meta-section">
          <button type="button" className="btn-subtle detail-note-meta-toggle" onClick={() => setNoteMetaExpanded(prev => !prev)}>
            {attrSummary}<span className="detail-note-meta-toggle-caret">{noteMetaExpanded ? ' ▼' : ' ▶'}</span>
          </button>
          {noteMetaExpanded && (
            <div className="detail-note-meta-grid">
              <div className="detail-note-meta-row">
                <AppSelect className="search-core-select" value={tune.genre || ''} onChange={(value) => updateTuneField('genre', value)} options={genreOptions.map((g) => ({ value: g, label: g }))} placeholder="Category" active={Boolean(tune.genre)} style={inputStyle} />
                <AppSelect className="search-core-select" value={tune.type || ''} onChange={(value) => updateTuneField('type', value)} options={typeOptions.map((t) => ({ value: t, label: t }))} placeholder="Type" active={Boolean(tune.type)} style={inputStyle} />
              </div>
              <div className="detail-note-meta-row">
                <AppSelect className="search-core-select" value={tune.status || ''} onChange={(value) => updateTuneField('status', value || '')} options={noteStatusSelectOptions} placeholder="Status (—)" active={Boolean(tune.status)} style={inputStyle} />
                <AppSelect className="search-core-select" value={tune.rating || ''} onChange={(value) => updateTuneField('rating', value || '')} options={noteRatingSelectOptions} placeholder="Rating (—)" active={Boolean(tune.rating)} style={inputStyle} />
              </div>
              {/* Links */}
              <div style={{ marginTop: '6px' }}>
                {allLinks.map((link, i) => (
                  <div key={i} className="detail-link-row" style={{ marginBottom: '4px' }}>
                    <a href={link} target="_blank" rel="noopener noreferrer" className="detail-link-go" title="Open link">🔗</a>
                    <input type="url" defaultValue={link} onBlur={(e) => { const val = e.target.value.trim(); if (val && val !== link) { const updated = [...allLinks]; updated[i] = val; saveLinks(updated); } }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} className={`search-input detail-field-input ${String(link || '').trim() ? 'detail-link-input-active' : ''}`} style={{ ...inputStyle, flex: 1, minWidth: 0, boxSizing: 'border-box', fontSize: '0.85rem' }} />
                    <button className="btn-subtle" onClick={() => saveLinks(allLinks.filter((_, j) => j !== i))} style={{ fontSize: '0.8rem', flexShrink: 0, padding: '2px 4px' }}>✕</button>
                  </div>
                ))}
                {showAddLinkField ? (
                  <div className="detail-link-row" style={{ marginTop: '2px' }}>
                    <input type="url" className="search-input detail-field-input" placeholder="Paste URL" value={pendingNewLink} onChange={(e) => setPendingNewLink(e.target.value)} onKeyDown={(e) => { if (e.key !== 'Enter') return; e.preventDefault(); const nextUrl = pendingNewLink.trim(); if (!nextUrl) return; saveLinks([...allLinks, nextUrl]); setPendingNewLink(''); setShowAddLinkField(false); }} style={{ ...inputStyle, flex: 1, minWidth: 0, boxSizing: 'border-box', fontSize: '0.85rem' }} autoFocus />
                    <button className="btn-subtle" onClick={() => { const nextUrl = pendingNewLink.trim(); if (!nextUrl) return; saveLinks([...allLinks, nextUrl]); setPendingNewLink(''); setShowAddLinkField(false); }} style={{ fontSize: '0.8rem', flexShrink: 0, padding: '2px 6px' }}>Add</button>
                    <button className="btn-subtle" onClick={() => { setPendingNewLink(''); setShowAddLinkField(false); }} style={{ fontSize: '0.8rem', flexShrink: 0, padding: '2px 6px' }}>Cancel</button>
                  </div>
                ) : (
                  <button className="btn-subtle" onClick={() => setShowAddLinkField(true)} style={{ fontSize: '0.85rem', marginTop: '2px' }}>+ Add link</button>
                )}
              </div>
            </div>
          )}
        </div>
      );
    })()}

    {/* Attachments collapsible */}
    {(() => {
      const fileCount = tune.attachments?.length || 0;
      const recCount = tune.recordings?.length || 0;
      const attachParts = [];
      if (fileCount > 0) attachParts.push(`${fileCount} file${fileCount !== 1 ? 's' : ''}`);
      if (recCount > 0) attachParts.push(`${recCount} recording${recCount !== 1 ? 's' : ''}`);
      const attachSummary = attachParts.length ? attachParts.join(' · ') : 'Attachments';
      return (
        <div className="detail-note-meta-section">
          <button type="button" className="btn-subtle detail-note-meta-toggle" onClick={() => setNoteAttachmentsExpanded(prev => !prev)}>
            {attachSummary}<span className="detail-note-meta-toggle-caret">{noteAttachmentsExpanded ? ' ▼' : ' ▶'}</span>
          </button>
          {noteAttachmentsExpanded && (
            <div style={{ marginTop: '8px' }}>
              {/* Existing files */}
              {fileCount > 0 && (
                <div className="detail-links-stack" style={{ marginBottom: '6px' }}>
                  {tune.attachments.map((file, fileIdx) => (
                    <div key={`${file.url || file.name || 'attachment'}-${fileIdx}`} className="detail-link-row">
                      <button type="button" className="detail-link-go" onClick={() => openAttachmentViewer(file)} title="Open attachment">📎</button>
                      <div className="search-input detail-field-input" style={{ ...inputStyle, flex: 1, minWidth: 0, boxSizing: 'border-box', display: 'flex', alignItems: 'center', opacity: 0.9 }} title={file.name || 'Attachment'} onClick={() => openAttachmentViewer(file)}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name || 'Attachment'}</span>
                      </div>
                      <button className="btn-subtle" onClick={() => setPendingAttachmentDelete({ file, fileIdx })} style={{ fontSize: '0.82rem', flexShrink: 0, padding: '3px 6px' }} title="Delete attachment">✕</button>
                    </div>
                  ))}
                </div>
              )}
              {/* Existing recordings — compact rows */}
              {recCount > 0 && (
                <ul style={{ listStyleType: 'none', paddingLeft: 0, maxWidth: '380px', margin: '0 auto 8px' }}>
                  {tune.recordings.map((rec, idx) => {
                    const audioUrl = getStableRecordingAudioUrl(rec);
                    return (
                      <li key={`${rec.id || 'rec'}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', flexShrink: 0 }}>{rec.label?.trim() || `#${idx + 1}`}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {audioUrl
                            ? <AudioPlayerWithSpeed src={audioUrl} compact />
                            : <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Uploading…</span>
                          }
                        </div>
                        <button className="btn-subtle" onClick={() => openTransferRecordingModal(rec)} style={{ fontSize: '0.7rem', padding: '2px 5px', flexShrink: 0 }} title={`Attach to ${transferTargetLabel}`}>↗</button>
                        <button className="btn-subtle" onClick={() => setRecordingToDelete(rec)} style={{ fontSize: '0.7rem', padding: '2px 5px', flexShrink: 0 }} title="Delete">🗑</button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {/* Add file / recorder */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '6px' }}>
                <button className="btn-subtle" onClick={() => detailFileInputRef.current?.click()} style={{ fontSize: '0.8rem' }}>+ Attach file</button>
                {!showRecorder && (
                  <button className="btn-subtle" onClick={() => setShowRecorder(true)} style={{ fontSize: '0.8rem' }}>+ Record</button>
                )}
              </div>
              {showRecorder && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0 4px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TuneRecorder tune={currentTune} user={user} onUpdateRecordings={(recs) => { setCurrentTune(prev => ({ ...prev, recordings: recs })); setLoadedTune(prev => ({ ...prev, recordings: recs })); setShowRecorder(false); }} />
                  </div>
                  <button className="btn-subtle" onClick={() => setShowRecorder(false)} style={{ fontSize: '0.8rem', padding: '2px 6px', flexShrink: 0 }}>✕</button>
                </div>
              )}
            </div>
          )}
        </div>
      );
    })()}

    {/* Info collapsible — shared with, appears in, timestamps */}
    {(() => {
      const toJsDate = (v) => v?.toDate ? v.toDate() : v;
      const addedDate = tune.createdAt ? formatShortUsDate(toJsDate(tune.createdAt)) : '';
      const modifiedDate = (tune.updatedAt && tune.updatedAt !== tune.createdAt) ? formatShortUsDate(toJsDate(tune.updatedAt)) : '';
      const infoParts = [
        addedDate && `Added ${addedDate}`,
        modifiedDate && `Modified ${modifiedDate}`,
      ].filter(Boolean);
      const infoSummary = infoParts.join(' · ') || 'Info';
      return (
        <div className="detail-note-meta-section">
          <button type="button" className="btn-subtle detail-note-meta-toggle" onClick={() => setNoteInfoExpanded(prev => !prev)}>
            {infoSummary}<span className="detail-note-meta-toggle-caret">{noteInfoExpanded ? ' ▼' : ' ▶'}</span>
          </button>
          {noteInfoExpanded && (
            <div style={{ marginTop: '6px' }}>
              {tune.source && (
                <p className="detail-meta-muted" style={{ textAlign: 'center' }}>
                  Source:{' '}
                  {tune.importedFrom?.uid ? (
                    <span style={{ color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate(`/search?sentBy=${tune.importedFrom.uid}&sentByName=${encodeURIComponent(tune.importedFrom.displayName || '')}`)}>
                      {tune.source}
                    </span>
                  ) : tune.source}
                </p>
              )}
              {Array.isArray(tune.sharedWith) && tune.sharedWith.length > 0 && (
                <p className="detail-meta-muted" style={{ textAlign: 'center' }}>
                  Shared with:{' '}
                  {tune.sharedWith.map((s, i) => (
                    <span key={s.uid}>
                      {i > 0 && ', '}
                      <span style={{ color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate(`/user/${s.uid}`)}>
                        {s.displayName || s.username || 'Unknown'}
                      </span>
                    </span>
                  ))}
                </p>
              )}
              {(relatedSets.length > 0 || validSessions.length > 0) && (
                <div className="detail-related-box" style={{ marginTop: '6px' }}>
                  <p className="detail-related-title"><strong>{isNotesMode ? 'This note appears in...' : 'This tune appears in...'}</strong></p>
                  {relatedSets.length > 0 && (
                    <div className="detail-related-section">
                      <p className="detail-related-title"><strong>Folders</strong></p>
                      <ul className="detail-related-list">
                        {relatedSets.filter(s => s?.setId).map((s, i) => (
                          <li key={`set-${i}`}><span className="detail-related-link" onClick={() => navigate(`/sets/${s.setId}`)}>{(s.title && !/^Untitled (Set|Folder)$/i.test(s.title)) ? `${s.title} — ${formatShortUsDate(s.createdAt)}` : `Untitled Folder — ${formatShortUsDate(s.createdAt)}`}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {validSessions.length > 0 && (
                    <div className="detail-related-section">
                      <p className="detail-related-title"><strong>Sessions</strong></p>
                      <ul className="detail-related-list">
                        {validSessions.map((s, i) => (
                          <li key={`session-${i}`}><span className="detail-related-link" onClick={() => navigate(`/session/${s.sessionId}`)}>{(s.location && s.location !== 'Unnamed') ? `${s.location} — ${formatShortUsDate(s.startedAt)}` : `Unnamed — ${formatShortUsDate(s.startedAt)}`}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <DetailTimestampsFooter createdAt={tune.createdAt} updatedAt={tune.updatedAt} lastViewedAt={tune.lastViewedAt} importedOn={tune.importedOn} />
            </div>
          )}
        </div>
      );
    })()}
  </>
) : (
  /* ===== TUNES MODE: inline editing layout ===== */
  <>
          <InlineEditableTitle
            isEditing={isEditingTitle}
            value={tuneDraft.title || ''}
            displayValue={tuneDraft.title || tune?.title || 'Untitled'}
            onChange={(nextTitle) => setTuneDraft((prev) => ({ ...prev, title: nextTitle }))}
            onStartEditing={() => setIsEditingTitle(true)}
            onCommit={async () => {
              await persistSimpleField('title', tuneDraft.title || '');
              setIsEditingTitle(false);
            }}
            onCancel={() => {
              setTuneDraft((prev) => ({ ...prev, title: tune?.title || '' }));
              setIsEditingTitle(false);
            }}
            rowClassName="inline-editable-title-row detail-tune-title-row"
            displayClassName="inline-editable-title-display detail-title-display detail-title-shell detail-title-heading"
            inputClassName="search-input inline-editable-title-input detail-title-input detail-title-shell detail-title-heading"
            inputStyle={{
              ...titleInputStyle,
              boxSizing: 'border-box',
            }}
          />

          <div className="detail-note-meta-grid detail-tune-meta-primary">
            <input
              className="search-input detail-field-input detail-quicknote-input"
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              placeholder="Quick note"
              value={tuneDraft.quickNote || ''}
              onChange={(e) => setTuneDraft((prev) => ({ ...prev, quickNote: e.target.value }))}
              onBlur={(e) => {
                const cleaned = stripFieldPrefix(e.target.value, 'Quicknote:');
                setTuneDraft((prev) => ({ ...prev, quickNote: cleaned }));
                persistSimpleField('quickNote', cleaned);
              }}
            />
            {showHints && (
              <p className="detail-meta-muted" style={{ textAlign: 'center', marginTop: '4px' }}>
                Quick note is your short memory jog shown in list previews.
              </p>
            )}
          </div>

          <div className="detail-tag-row">
            <DetailTagEditor
              tags={tune.customTags || []}
              onChange={(tags) => updateTuneField('customTags', tags)}
              suggestions={tagSuggestions}
              inputStyle={inputStyle}
              onTagClick={navigateToTagSearch}
              containerStyle={{ margin: '0 auto' }}
            />
            {showHints && (
              <p className="detail-meta-muted" style={{ textAlign: 'center', marginTop: '4px' }}>
                Tags are clickable and can reset/filter your search context.
              </p>
            )}
          </div>

          {hasValue(tuneDraft.imageUrl) && (
          <div style={{ margin: '0.75rem 0', display: 'flex', justifyContent: 'center' }}>
            <img
              src={tuneDraft.imageUrl}
              alt=""
              onClick={() => setFullscreenImgUrl(tuneDraft.imageUrl)}
              style={{
                maxWidth: '100%',
                maxHeight: '500px',
                borderRadius: '8px',
                border: '1px solid var(--border-color, #ccc)',
                cursor: 'pointer',
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          )}
          {topAttachmentImages.length > 0 && (
            <div style={{ margin: '0.75rem 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              {topAttachmentImages.map((file, idx) => (
                <img
                  key={file.url || `${file.name || 'attachment'}-${idx}`}
                  src={file.url}
                  alt={file.name || ''}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '500px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #ccc)',
                    cursor: 'pointer',
                  }}
                  onClick={() => setFullscreenImgUrl(file.url)}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ))}
            </div>
          )}

          <div className="detail-longfield-details detail-lyrics-section">
            <button
              type="button"
              className="btn-subtle search-control-btn detail-header-pill"
              aria-expanded={isLyricsDetailsOpen}
              onClick={() => setIsLyricsDetailsOpen((prev) => !prev)}
            >
              <span className="detail-header-pill-caret">{isLyricsDetailsOpen ? '▼' : '▶'}</span>
              <span>Lyrics/Chords</span>
            </button>
            {isLyricsDetailsOpen && (
              <div className="detail-pill-content">
                {showHints && (
                  <p className="detail-meta-muted" style={{ textAlign: 'center', marginBottom: '6px' }}>
                    Tap to open fullscreen lyrics/chords editor in monospace.
                  </p>
                )}
                <div
                  className="detail-lyrics-card"
                  onClick={() => setFullscreenContent('lyrics')}
                  title="Tap to open fullscreen lyrics editor"
                >
                  {hasValue(tuneLongDraft.lyrics) ? (
                    <pre className="detail-lyrics-preview">{tuneLongDraft.lyrics}</pre>
                  ) : (
                    <span className="detail-lyrics-placeholder">Tap to add lyrics/chords...</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="detail-longfield-details" style={{ marginTop: '0.75rem' }}>
            <button
              type="button"
              className="btn-subtle search-control-btn detail-header-pill"
              aria-expanded={isAbcDetailsOpen}
              onClick={() => setIsAbcDetailsOpen((prev) => !prev)}
            >
              <span className="detail-header-pill-caret">{isAbcDetailsOpen ? '▼' : '▶'}</span>
              <span>ABC Notation</span>
            </button>
            {isAbcDetailsOpen && (
              <div className={`detail-pill-content ${(isRawAbcOpen || tuneImportedFromSession || importedTune || sessionSuggestions.length > 0 || sessionSearchAttempted) ? 'detail-pill-content-with-tail' : 'detail-pill-content-no-tail'}`}>
                {showHints && (
                  <p className="detail-meta-muted" style={{ textAlign: 'center', marginBottom: '6px' }}>
                    Preview notation, play ABC, then open Raw ABC to edit source text.
                  </p>
                )}
                <div className="detail-abc-preview-wrap">
                  <div
                    onClick={() => setFullscreenContent('abc')}
                    className="detail-abc-preview-tap"
                    title="Tap to view fullscreen"
                  >
                    <div className="detail-abc-preview-inner">
                      {hasValue(tuneLongDraft.abc) ? (
                        <AbcNotation abc={tuneLongDraft.abc} />
                      ) : (
                        <div className="detail-abc-placeholder">No ABC yet. Use Show Raw ABC to add.</div>
                      )}
                    </div>
                  </div>
                  {hasValue(tuneLongDraft.abc) && <AbcMidiPlayer abc={tuneLongDraft.abc} />}
                </div>
                <div
                  className="detail-abc-raw-details"
                  style={{ marginBottom: tuneImportedFromSession ? '0.55rem' : 0 }}
                >
                  <button
                    type="button"
                    className="btn-subtle search-control-btn detail-header-pill detail-header-pill-subheading"
                    aria-expanded={isRawAbcOpen}
                    onClick={() => setIsRawAbcOpen((prev) => !prev)}
                  >
                    <span className="detail-header-pill-caret">{isRawAbcOpen ? '▼' : '▶'}</span>
                    <span>Show Raw ABC</span>
                  </button>
                      {tuneImportedFromSession && (
                        <div style={{ marginBottom: '8px', textAlign: 'center' }}>
                      <button className="btn-subtle search-control-btn detail-session-update-btn" onClick={fetchSessionSuggestions}>
                        Update from thesession.org
                      </button>
                      {showHints && (
                        <p className="detail-meta-muted" style={{ marginTop: '4px' }}>
                          import directly by choosing from pop-up matches below
                        </p>
                      )}
                    </div>
                  )}
                  {sessionSearchAttempted && sessionSearchLoading && (
                    <p className="detail-meta-muted" style={{ textAlign: 'center', marginTop: '8px' }}>
                      Searching TheSession.org...
                    </p>
                  )}
                  {sessionSearchAttempted && !sessionSearchLoading && sessionSuggestions.length === 0 && (
                    <p className="detail-meta-muted" style={{ textAlign: 'center', marginTop: '8px' }}>
                      No matches found on TheSession.org for "{tuneDraft?.title || tune?.title || ''}"
                    </p>
                  )}
                  {sessionSuggestions.length > 0 && !dismissSessionSuggestions && (
                    <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                        <p className="detail-meta-muted" style={{ textAlign: 'center', margin: 0 }}>
                          Matches from TheSession
                        </p>
                        <button
                          type="button"
                          className="btn-subtle detail-session-dismiss-btn"
                          onClick={() => setDismissSessionSuggestions(true)}
                          aria-label="Dismiss session suggestions"
                        >
                          ×
                        </button>
                      </div>
                      <div className="detail-note-meta-grid">
                        {sessionSuggestions.map((match) => (
                          <button
                            key={match.id}
                            className="btn-subtle search-control-btn detail-session-suggestion-btn"
                            onClick={() => importFromSession(match.id)}
                          >
                            {match.name} ({match.type})
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {importedTune && (
                    <div
                      className="detail-session-import-panel"
                      style={{
                        margin: '0 auto 10px',
                        padding: '10px 12px',
                        borderRadius: '10px',
                        border: '1px solid color-mix(in srgb, var(--card-border) 72%, transparent)',
                        background: 'color-mix(in srgb, var(--bg-card) 86%, var(--button-bg) 14%)'
                      }}
                    >
                      <div style={{ textAlign: 'center', fontSize: 'var(--ui-compact-font)', fontWeight: 700, marginBottom: '6px' }}>
                        Review Import
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        <button className="btn-subtle search-control-btn" onClick={applyImportedSessionFields}>
                          Apply Import
                        </button>
                        <button className="btn-subtle search-control-btn" onClick={() => setImportedTune(null)}>
                          Cancel
                        </button>
                      </div>
                      {Object.keys(importFields).map((field) => (
                        <label key={field} className="detail-meta-muted" style={{ display: 'block', marginBottom: '6px' }}>
                          <input
                            type="checkbox"
                            checked={importFields[field]}
                            onChange={(e) => setImportFields((prev) => ({ ...prev, [field]: e.target.checked }))}
                          />
                          {' '}
                          {field === 'abc' ? 'ABC notation' : field}
                        </label>
                      ))}
                      <AbcImportPreview
                        importedTune={importedTune}
                        setImportedTune={setImportedTune}
                        importFields={importFields}
                        customAbcOverride={customAbcOverride}
                        setCustomAbcOverride={setCustomAbcOverride}
                        setOriginalAbc={setOriginalAbc}
                        abcCompact={false}
                      />
                    </div>
                  )}
                  {isRawAbcOpen && (
                    <div className="detail-pill-subcontent">
                      <textarea
                        ref={rawAbcTextareaRef}
                        className="search-input add-entry-textarea add-abc-field detail-longfield-textarea"
                        value={tuneLongDraft.abc}
                        onChange={(e) => {
                          setTuneLongDraft((prev) => ({ ...prev, abc: e.target.value }));
                          autoSizeRawAbcTextarea(e.target);
                        }}
                        placeholder={'M:4/4\nL:1/8\nK:Cmaj\n| notes here |'}
                        style={{ minHeight: '220px', overflowY: 'hidden', resize: 'none' }}
                      />
                      {tuneModeAbcDirty && (
                        <div className="detail-edit-actions">
                          <button className="btn-subtle search-control-btn detail-action-btn" onClick={() => saveLongField('abc')}>Save ABC</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="detail-longfield-details">
            <button
              type="button"
              className="btn-subtle search-control-btn detail-header-pill"
              aria-expanded={isGeneralInfoOpen}
              onClick={() => setIsGeneralInfoOpen((prev) => !prev)}
            >
              <span className="detail-header-pill-caret">{isGeneralInfoOpen ? '▼' : '▶'}</span>
              <span>General Info</span>
            </button>
            {isGeneralInfoOpen && (
              <div className="detail-pill-content">
                <div className="detail-note-meta-grid detail-tune-files-row">
                  <div className="detail-note-meta-row">
                    <AppSelect
                      className="search-core-select"
                      value={tuneDraft.genre || ''}
                      onChange={(value) => {
                        setTuneDraft((prev) => ({ ...prev, genre: value || '' }));
                        persistSimpleField('genre', value || '');
                      }}
                      options={genreOptions.map((g) => ({ value: g, label: g }))}
                      placeholder="Genre"
                      active={Boolean(tuneDraft.genre)}
                      style={inputStyle}
                    />
                    <AppSelect
                      className="search-core-select"
                      value={tuneDraft.type || ''}
                      onChange={(value) => {
                        setTuneDraft((prev) => ({ ...prev, type: value || '' }));
                        persistSimpleField('type', value || '');
                      }}
                      options={typeOptions.map((t) => ({ value: t, label: t }))}
                      placeholder="Type"
                      active={Boolean(tuneDraft.type)}
                      style={inputStyle}
                    />
                  </div>
                  <div className="detail-note-meta-row">
                    <div style={{ minWidth: 0 }}>
                      <KeyInput
                        value={tuneDraft.key || ''}
                        onChange={(e) => setTuneDraft((prev) => ({ ...prev, key: e.target.value }))}
                        onBlur={(val) => {
                          const normalized = normalizeKey(val || '');
                          setTuneDraft((prev) => ({ ...prev, key: normalized }));
                          persistSimpleField('key', normalized);
                        }}
                        suggestions={keyRawSuggestions}
                        inputStyle={{ ...inputStyle }}
                      />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <input
                        className="search-input detail-field-input"
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                        placeholder="Composer"
                        value={tuneDraft.composer || ''}
                        onChange={(e) => setTuneDraft((prev) => ({ ...prev, composer: e.target.value }))}
                        onBlur={(e) => {
                          const cleaned = stripFieldPrefix(e.target.value, 'Composer:');
                          setTuneDraft((prev) => ({ ...prev, composer: cleaned }));
                          persistSimpleField('composer', cleaned);
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="detail-note-meta-grid detail-tune-links-row">
                  <div className="detail-note-meta-row">
                    <AppSelect
                      className="search-core-select"
                      value={tuneDraft.status || ''}
                      onChange={(value) => {
                        setTuneDraft((prev) => ({ ...prev, status: value || '' }));
                        persistSimpleField('status', value || '');
                      }}
                      options={noteStatusSelectOptions}
                      placeholder="Status (—)"
                      active={Boolean(tuneDraft.status)}
                      style={inputStyle}
                    />
                    <AppSelect
                      className="search-core-select"
                      value={tuneDraft.rating || ''}
                      onChange={(value) => {
                        setTuneDraft((prev) => ({ ...prev, rating: value || '' }));
                        persistSimpleField('rating', value || '');
                      }}
                      options={noteRatingSelectOptions}
                      placeholder="Rating (—)"
                      active={Boolean(tuneDraft.rating)}
                      style={inputStyle}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="detail-longfield-details">
            <button
              type="button"
              className="btn-subtle search-control-btn detail-header-pill"
              aria-expanded={isTuneNotesDetailsOpen}
              onClick={() => setIsTuneNotesDetailsOpen((prev) => !prev)}
            >
              <span className="detail-header-pill-caret">{isTuneNotesDetailsOpen ? '▼' : '▶'}</span>
              <span>Notes</span>
            </button>
            {isTuneNotesDetailsOpen && (
              <div className="detail-pill-content">
                <div
                  className="detail-lyrics-card"
                  onClick={() => setFullscreenContent('notes')}
                  title="Tap to open fullscreen notes editor"
                >
                  {hasValue(tuneLongDraft.notes) ? (
                    <pre className="detail-lyrics-preview">{tuneLongDraft.notes}</pre>
                  ) : (
                    <span className="detail-lyrics-placeholder">Tap to add notes...</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="detail-longfield-details">
            <button
              type="button"
              className="btn-subtle search-control-btn detail-header-pill"
              aria-expanded={isAttachmentsSectionOpen}
              onClick={() => setIsAttachmentsSectionOpen((prev) => !prev)}
            >
              <span className="detail-header-pill-caret">{isAttachmentsSectionOpen ? '▼' : '▶'}</span>
              <span>Attachments</span>
            </button>
            {isAttachmentsSectionOpen && (
              <div className="detail-pill-content">
                {showHints && (
                  <p className="detail-meta-muted" style={{ textAlign: 'center', marginBottom: '6px' }}>
                    Add files, links, image URL, and recordings in one place.
                  </p>
                )}
                <div className="detail-note-meta-grid detail-tune-image-row">
                  <input
                    ref={detailFileInputRef}
                    type="file"
                    accept={ATTACHMENT_ACCEPT_ATTR}
                    multiple
                    onChange={handleDetailFileUpload}
                    className="detail-file-input search-input add-file-input"
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                {Array.isArray(tune.attachments) && tune.attachments.length > 0 && (
                  <div className="detail-note-meta-grid" style={{ marginTop: '6px' }}>
                    <div className="detail-links-stack">
                      {tune.attachments.map((file, fileIdx) => (
                        <div key={`${file.url || file.name || 'attachment'}-${fileIdx}`} className="detail-link-row">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="detail-link-go"
                            title="Open attachment"
                          >
                            📎
                          </a>
                          <div
                            className="search-input detail-field-input"
                            style={{
                              ...inputStyle,
                              flex: 1,
                              minWidth: 0,
                              boxSizing: 'border-box',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: 0.9,
                            }}
                            title={file.name || 'Attachment'}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name || 'Attachment'}
                            </span>
                          </div>
                          <button
                            className="btn-subtle"
                            onClick={() => setPendingAttachmentDelete({ file, fileIdx })}
                            style={{ fontSize: '0.82rem', flexShrink: 0, padding: '3px 6px' }}
                            title="Delete attachment"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="detail-note-meta-grid">
                  {(() => {
                    const allLinks = [];
                    [tune.link1, tune.link2, tune.link3].forEach((l) => { if (l?.trim()) allLinks.push(l.trim()); });
                    (tune.links || []).forEach((l) => { if (l?.trim() && !allLinks.includes(l.trim())) allLinks.push(l.trim()); });

                    const saveLinks = async (updated) => {
                      const cleaned = updated.map((l) => String(l || '').trim()).filter(Boolean);
                      const tuneRef = doc(db, 'users', user.uid, 'tunes', tune.id);
                      const nowIso = new Date().toISOString();
                      await updateDoc(tuneRef, {
                        link1: cleaned[0] || '',
                        link2: cleaned[1] || '',
                        link3: cleaned[2] || '',
                        links: cleaned,
                        updatedAt: nowIso,
                      });
                      setLoadedTune(prev => prev ? {
                        ...prev,
                        link1: cleaned[0] || '',
                        link2: cleaned[1] || '',
                        link3: cleaned[2] || '',
                        links: cleaned,
                        updatedAt: nowIso,
                      } : prev);
                    };

                    return (
                      <div className="detail-links-stack">
                        {allLinks.map((link, i) => (
                          <div key={i} className="detail-link-row">
                            <a href={link} target="_blank" rel="noopener noreferrer" className="detail-link-go" title="Open link">🔗</a>
                            <input
                              type="url"
                              defaultValue={link}
                              onBlur={(e) => {
                                const val = e.target.value.trim();
                                if (val && val !== link) {
                                  const updated = [...allLinks];
                                  updated[i] = val;
                                  saveLinks(updated);
                                }
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              className={`search-input detail-field-input ${String(link || '').trim() ? 'detail-link-input-active' : ''}`}
                              style={{ ...inputStyle, flex: 1, minWidth: 0, boxSizing: 'border-box' }}
                            />
                            <button
                              className="btn-subtle"
                              onClick={() => saveLinks(allLinks.filter((_, j) => j !== i))}
                              style={{ fontSize: '0.82rem', flexShrink: 0, padding: '3px 6px' }}
                            >✕</button>
                          </div>
                        ))}
                        {showAddLinkField ? (
                          <div className="detail-link-row">
                            <input
                              type="url"
                              className="search-input detail-field-input"
                              placeholder="Paste URL"
                              value={pendingNewLink}
                              onChange={(e) => setPendingNewLink(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key !== 'Enter') return;
                                e.preventDefault();
                                const nextUrl = pendingNewLink.trim();
                                if (!nextUrl) return;
                                saveLinks([...allLinks, nextUrl]);
                                setPendingNewLink('');
                                setShowAddLinkField(false);
                              }}
                              style={{ ...inputStyle, flex: 1, minWidth: 0, boxSizing: 'border-box' }}
                              autoFocus
                            />
                            <button
                              className="btn-subtle"
                              onClick={() => {
                                const nextUrl = pendingNewLink.trim();
                                if (!nextUrl) return;
                                saveLinks([...allLinks, nextUrl]);
                                setPendingNewLink('');
                                setShowAddLinkField(false);
                              }}
                              style={{ fontSize: '0.82rem', flexShrink: 0, padding: '3px 6px' }}
                            >
                              Add
                            </button>
                            <button
                              className="btn-subtle"
                              onClick={() => {
                                setPendingNewLink('');
                                setShowAddLinkField(false);
                              }}
                              style={{ fontSize: '0.82rem', flexShrink: 0, padding: '3px 6px' }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn-subtle detail-links-add"
                            onClick={() => setShowAddLinkField(true)}
                            style={{ fontSize: '0.85rem' }}
                          >
                            + Add link
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="detail-note-meta-grid">
                  <input
                    className="search-input detail-field-input"
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                    placeholder="Image URL"
                    value={tuneDraft.imageUrl || ''}
                    onChange={(e) => setTuneDraft((prev) => ({ ...prev, imageUrl: e.target.value }))}
                    onBlur={(e) => persistSimpleField('imageUrl', e.target.value)}
                  />
                </div>

                <div className="detail-recorder-wrap detail-tune-recorder-row" style={{ width: '100%', maxWidth: '300px' }}>
                  <TuneRecorder
                    tune={currentTune}
                    user={user}
                    onUpdateRecordings={(recs) => {
                      setCurrentTune(prev => ({ ...prev, recordings: recs }));
                      setLoadedTune(prev => ({ ...prev, recordings: recs }));
                    }}
                    containerStyle={{
                      textAlign: 'center'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="detail-longfield-details">
            <button
              type="button"
              className="btn-subtle search-control-btn detail-header-pill"
              aria-expanded={isSearchToolsOpen}
              onClick={() => setIsSearchToolsOpen((prev) => !prev)}
            >
              <span className="detail-header-pill-caret">{isSearchToolsOpen ? '▼' : '▶'}</span>
              <span>Search Tools</span>
            </button>
            {isSearchToolsOpen && (
              <div className="detail-pill-content">
                {showHints && (
                  <p className="detail-meta-muted" style={{ textAlign: 'center', marginBottom: '6px' }}>
                    Search external sites, then import from thesession.org by URL or ID.
                  </p>
                )}
                <div className="detail-search-tools-grid">
                  {orderedTunePageSearchTools.map((tool) => (
                    <div key={tool.id || tool.url} style={{ textAlign: 'center' }}>
                      <button
                        className="btn-subtle search-control-btn"
                        style={{ width: '100%' }}
                        onClick={() => openSearchTool(tool.url)}
                      >
                        {tool.url}
                      </button>
                      {showHints && tool.url === 'tunearch.org' && (
                        <p className="detail-meta-muted" style={{ marginTop: '4px' }}>
                          copy full ABC and paste above
                        </p>
                      )}
                      {showHints && tool.url === 'bluegrasslyrics.com' && (
                        <p className="detail-meta-muted" style={{ marginTop: '4px' }}>
                          copy lyrics and paste above
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {sessionToolEnabled && (
                  <>
                    <div className="detail-note-meta-grid" style={{ marginTop: '8px' }}>
                      <div className="detail-link-row">
                        <input
                          className="search-input detail-field-input"
                          type="text"
                          value={sessionInput}
                          onChange={(e) => setSessionInput(e.target.value)}
                          placeholder="Paste thesession.org URL or ID"
                          style={{ ...inputStyle, flex: 1, minWidth: 0, boxSizing: 'border-box' }}
                        />
                        <button className="btn-subtle search-control-btn" onClick={handleImportFromPastedSessionId}>
                          Import
                        </button>
                      </div>
                    </div>
                    {showHints && (
                      <p className="detail-meta-muted" style={{ textAlign: 'center', marginTop: '6px' }}>
                        Paste thesession.org tuneID or url and tap Import. On phone, tap Share -> Copy to grab the link.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
  </>
)}

          {!isNotesMode && tune.source && (
            <p className="detail-meta-line">
              <strong>Source:</strong>{' '}
              {tune.importedFrom?.uid ? (
                <span
                  style={{ color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => navigate(`/search?sentBy=${tune.importedFrom.uid}&sentByName=${encodeURIComponent(tune.importedFrom.displayName || '')}`)}
                >
                  {tune.source}
                </span>
              ) : (
                tune.source
              )}
            </p>
          )}

          {!isNotesMode && Array.isArray(tune.sharedWith) && tune.sharedWith.length > 0 && (
            <p className="detail-meta-muted">
              Shared with:{' '}
              {tune.sharedWith.map((s, i) => (
                <span key={s.uid}>
                  {i > 0 && ', '}
                  <span
                    style={{ color: 'var(--text-link)', textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => navigate(`/user/${s.uid}`)}
                  >
                    {s.displayName || s.username || 'Unknown'}
                  </span>
                </span>
              ))}
            </p>
          )}
       



    {/* Fullscreen PDF Viewer */}
    {fullscreenPdfUrl && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1000,
          overflow: 'hidden'
        }}
      >
        <button
          onClick={() => setFullscreenPdfUrl(null)}
          style={{
            position: 'absolute',
            bottom: '1.5rem',
            right: '1.5rem',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#fff',
            color: '#000',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            border: 'none',
            cursor: 'pointer',
            zIndex: 1001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
          }}
        >
          ✕
        </button>
        <iframe
          src={fullscreenPdfUrl}
          title="Fullscreen PDF"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            backgroundColor: 'white'
          }}
        />
      </div>
    )}

    {/* Fullscreen Image Viewer */}
    {fullscreenImgUrl && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          zIndex: 1000,
          overflow: 'hidden'
        }}
      >
        <button
          onClick={() => setFullscreenImgUrl(null)}
          style={{
            position: 'absolute',
            bottom: '1.5rem',
            right: '1.5rem',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: '#fff',
            color: '#000',
            fontWeight: 'bold',
            fontSize: '1.2rem',
            border: 'none',
            cursor: 'pointer',
            zIndex: 1001,
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
          }}
        >
          ✕
        </button>
        <img
          src={fullscreenImgUrl}
          alt="Fullscreen View"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            maxWidth: '95%',
            maxHeight: '95%',
            transform: 'translate(-50%, -50%)',
            borderRadius: '8px',
            boxShadow: '0 0 12px rgba(0,0,0,0.5)'
          }}
        />
      </div>
    )}
{fullscreenContent && (
  <FullscreenViewer
    type={fullscreenContent}
    tune={tune}
    tunes={navList}
    currentIndex={navPosition}
    contextLabel={navContextLabel}
    contextName={navContextName}
    onClose={() => setFullscreenContent(null)}
    onNavigate={(newIndex) => {
      const target = navList[newIndex];
      if (!target?.id) return;
      if (navContext === 'set') {
        navigate(`/tune/${target.id}`, {
          state: { tune: target, setId, setTitle, setTunes: navList },
          replace: true,
        });
        return;
      }
      if (navContext === 'session') {
        navigate(`/tune/${target.id}`, {
          state: { tune: target, sessionTunes: navList, sessionTitle },
          replace: true,
        });
      }
    }}
    onLyricsChange={(newLyrics) => {
      updateTuneField('lyrics', newLyrics);
    }}
    onNotesChange={(newNotes) => {
      updateTuneField('notes', newNotes);
    }}
  />
)}

<FullscreenNotesEditorOverlay
  open={fullscreenNotes && isEditingNotes}
  editorRef={notesEditorRef}
  titleValue={editTitleValue}
  onTitleChange={setEditTitleValue}
  notesValue={editNotesValue}
  onNotesChange={handleNotesEditorChange}
  placeholder="Write your note..."
  onClose={() => {
    flushNotesAutosaveNow().catch(() => {});
    setFullscreenNotes(false);
    setFullscreenNotesDistractionFree(false);
    setNotesUiMode('edit');
  }}
  toolbarCollapsed={fullscreenNotesDistractionFree}
  onToolbarCollapsedChange={(next) => {
    const active = Boolean(next);
    setFullscreenNotesDistractionFree(active);
    setNotesUiMode(active ? 'zen' : 'focus');
  }}
  inputStyle={inputStyle}
  enableInternalLinks={isNotesMode}
  internalLinkTargets={internalLinkTargets}
  internalLinkNotesById={embedNotesById}
  onInternalLinkNavigate={navigateInternalLink}
  onForkNoteRequested={isNotesMode ? handleForkNoteRequest : null}
  showForkButton={isNotesMode}
/>

<CreateLinkedNoteModal
  open={forkModalOpen}
  initialTitle={forkTitleCandidate}
  hasParentTags={Array.isArray(tune?.customTags) && tune.customTags.length > 0}
  busy={forkBusy}
  onConfirm={handleConfirmForkNote}
  onCancel={handleCancelForkNote}
/>

          {!isNotesMode && Array.isArray(tune.recordings) && tune.recordings.length > 0 && (
          <>
          <h4 style={{ marginTop: '0.75rem', marginBottom: '0.25rem' }}>Recordings</h4>
{transferNotice && (
  <p style={{ ...textStyle, textAlign: 'center', marginBottom: '0.5rem' }}>{transferNotice}</p>
)}
<ul style={{ listStyleType: 'none', paddingLeft: 0, maxWidth: '300px', margin: '0 auto' }}>
  {tune.recordings.map((rec, idx) => {
    const audioUrl = getStableRecordingAudioUrl(rec);
    return (
      <li key={`${rec.id || 'rec'}-${idx}`} style={{ marginBottom: '1rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.9rem', marginBottom: '4px' }}>{rec.label?.trim() || `Recording ${idx + 1}`}</div>
        {audioUrl ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <AudioPlayerWithSpeed src={audioUrl} compact />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '6px' }}>
              <button
                className="btn-subtle"
                onClick={() => openTransferRecordingModal(rec)}
                style={{ fontSize: '0.82rem', padding: '4px 10px', lineHeight: 1.1 }}
              >
                {`Attach to ${transferTargetLabel}`}
              </button>
              <button
                className="btn-subtle"
                onClick={() => setRecordingToDelete(rec)}
                style={{ fontSize: '0.82rem', padding: '4px 10px', lineHeight: 1.1 }}
              >
                🗑 Delete
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <em style={{ color: 'red' }}>No audio source yet</em>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '6px' }}>
              <button
                className="btn-subtle"
                onClick={() => openTransferRecordingModal(rec)}
                style={{ fontSize: '0.82rem', padding: '4px 10px', lineHeight: 1.1 }}
              >
                {`Attach to ${transferTargetLabel}`}
              </button>
              <button
                className="btn-subtle"
                onClick={() => setRecordingToDelete(rec)}
                style={{ fontSize: '0.82rem', padding: '4px 10px', lineHeight: 1.1 }}
              >
                🗑 Delete
              </button>
            </div>
          </div>
        )}
      </li>
    );
  })}
</ul>
          </>
          )}

{isNotesMode && (
  <InternalBacklinksPanel
    backlinks={noteBacklinks}
    onNavigate={(item) => navigate(item.href, {
      state: buildInternalLinkNavState(),
    })}
  />
)}

{!isNotesMode && (relatedSets.length > 0 || validSessions.length > 0) && (
  <div className="detail-related-box">
    <p className="detail-related-title">
      <strong>{isNotesMode ? 'This note appears in...' : 'This tune appears in...'}</strong>
    </p>
    {relatedSets.length > 0 && (
      <div className="detail-related-section">
        <p className="detail-related-title"><strong>{isNotesMode ? 'Folders' : 'Sets'}</strong></p>
        <ul className="detail-related-list">
          {relatedSets
            .filter(s => s?.setId)
            .map((s, i) => {
              const formattedDate = formatShortUsDate(s.createdAt);

              return (
                <li key={`set-${i}`}>
                  <span
                    className="detail-related-link"
                    onClick={() => navigate(`/sets/${s.setId}`)}
                  >
                    {(s.title && !/^Untitled (Set|Folder)$/i.test(s.title))
                      ? `${s.title} — ${formattedDate}`
                      : `Untitled ${isNotesMode ? 'Folder' : (labels?.Collection || 'Set')} — ${formattedDate}`}
                  </span>
                </li>
              );
            })}
        </ul>
      </div>
    )}

    {validSessions.length > 0 && (
      <div className="detail-related-section">
        <p className="detail-related-title"><strong>Sessions</strong></p>
        <ul className="detail-related-list">
          {validSessions.map((s, i) => {
            const formattedDate = formatShortUsDate(s.startedAt);

            return (
              <li key={`session-${i}`}>
                <span
                  className="detail-related-link"
                  onClick={() => navigate(`/session/${s.sessionId}`)}
                >
                  {(s.location && s.location !== 'Unnamed')
                    ? `${s.location} — ${formattedDate}`
                    : `Unnamed — ${formattedDate}`}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    )}
  </div>
)}

{!isNotesMode && (
  <DetailTimestampsFooter
    createdAt={tune.createdAt}
    updatedAt={tune.updatedAt}
    lastViewedAt={tune.lastViewedAt}
    importedOn={tune.importedOn}
  />
)}

    {/* Delete */}
    <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingBottom: '0.5rem' }}>
      <button
        className="btn-subtle"
        onClick={() => setConfirmDeleteTune(true)}
        style={{ color: '#e03131', fontSize: '0.75rem', opacity: 0.55 }}
      >
        Delete {isNotesMode ? 'Note' : 'Tune'}
      </button>
    </div>

        </div>
      </div>

      <ActionConfirmModal
        open={confirmDeleteTune}
        title={`Delete ${isNotesMode ? 'Note' : 'Tune'}`}
        message={`Permanently delete "${tune?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={handleDeleteTune}
        onCancel={() => setConfirmDeleteTune(false)}
      />

      <ActionConfirmModal
        open={Boolean(recordingToDelete)}
        title="Delete Recording"
        message="Delete this recording? This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={handleConfirmDeleteRecording}
        onCancel={() => setRecordingToDelete(null)}
      />

      <ActionConfirmModal
        open={Boolean(pendingModeChange)}
        title="Change Item Mode"
        message={`Move this ${itemLabels?.item || 'entry'} to ${pendingModeChange === 'tunes' ? 'Tunes' : 'Notes'} mode?`}
        confirmLabel="Move"
        cancelLabel="Cancel"
        onConfirm={async () => {
          if (!pendingModeChange) return;
          await updateTuneField('mode', pendingModeChange);
          setPendingModeChange(null);
        }}
        onCancel={() => setPendingModeChange(null)}
      />

      <ActionConfirmModal
        open={Boolean(pendingTagSearchTag)}
        title="Reset Filters"
        message="Filters are currently active. Reset filters and search by this tag instead?"
        confirmLabel="Search by Tag"
        cancelLabel="Cancel"
        onConfirm={() => {
          const tag = pendingTagSearchTag;
          setPendingTagSearchTag(null);
          if (!tag) return;
          const params = new URLSearchParams();
          params.set('tag', tag);
          navigate(`/search?${params.toString()}`);
        }}
        onCancel={() => setPendingTagSearchTag(null)}
      />

      <ActionConfirmModal
        open={Boolean(pendingAttachmentDelete)}
        title="Delete Attachment"
        message={pendingAttachmentDelete ? `Delete "${pendingAttachmentDelete.file.name}"?` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        onConfirm={async () => {
          if (!pendingAttachmentDelete) return;
          const { file, fileIdx } = pendingAttachmentDelete;
          if (file.path) {
            try {
              await deleteObject(storageRef(storage, file.path));
            } catch (err) {
              console.warn('Failed to delete attachment from storage:', err);
            }
          }
          const remaining = tune.attachments.filter((_, i) => i !== fileIdx);
          await updateTuneField('attachments', remaining);
          setPendingAttachmentDelete(null);
        }}
        onCancel={() => setPendingAttachmentDelete(null)}
      />

      {showTransferModal && (
        <div className="action-confirm-overlay">
          <div className="action-confirm-card" style={{ background: 'var(--card-bg, #fff)', color: 'var(--text-color, #000)' }}>
            <h3 className="action-confirm-title" style={{ marginBottom: '10px' }}>{`Attach Recording to ${transferTargetLabel}`}</h3>
            <p style={{ marginBottom: '12px' }}>
              {recordingToTransfer?.label ? `Recording: ${recordingToTransfer.label}` : `Select destination ${transferTargetLabelLower}`}
            </p>
            <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              {`Eligible ${transferTargetLabelLower}s shown below already include this tune.`}
            </p>
            <select
              style={{ ...inputStyle, width: '100%', marginBottom: '10px' }}
              value={transferSetId}
              onChange={(e) => setTransferSetId(e.target.value)}
            >
              {transferSets.map((s) => (
                <option key={s.id} value={s.id}>
                  {(s.title || `Untitled ${transferTargetLabel}`) + ' — ' + ((s.tunes || []).map(t => t?.title).filter(Boolean).slice(0, 4).join(', ') || 'No tune names')}
                </option>
              ))}
              <option value="__new__">{`+ Create New ${transferTargetLabel} with this tune`}</option>
            </select>

            <div style={{ marginBottom: '12px', textAlign: 'left' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '0.9rem' }}><strong>Choose action</strong></p>
              <label style={{ display: 'block', marginBottom: '4px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={transferMode === 'copy'}
                  onChange={() => setTransferMode('copy')}
                  style={{ marginRight: '6px' }}
                />
                {`Copy: keep recording on this tune and attach to ${transferTargetLabelLower}`}
              </label>
              <label style={{ display: 'block', cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={transferMode === 'move'}
                  onChange={() => setTransferMode('move')}
                  style={{ marginRight: '6px' }}
                />
                {`Move: remove from this tune and attach to ${transferTargetLabelLower}`}
              </label>
            </div>

            <div className="action-confirm-actions">
              <button
                className="btn-subtle action-confirm-cancel"
                onClick={() => {
                  setShowTransferModal(false);
                  setRecordingToTransfer(null);
                }}
                disabled={transferBusy}
              >
                Cancel
              </button>
              <button
                className={`btn-subtle ${transferMode === 'move' ? 'action-confirm-danger' : 'action-confirm-primary'}`}
                onClick={handleTransferRecording}
                disabled={transferBusy || !transferSetId}
              >
                {transferBusy ? 'Working...' : (transferMode === 'move' ? `Move to ${transferTargetLabel}` : `Attach to ${transferTargetLabel}`)}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddToSetPicker && (
        <div className="action-confirm-overlay">
          <div className="action-confirm-card" style={{ background: 'var(--card-bg, #fff)', color: 'var(--text-color, #000)', maxWidth: '480px' }}>
            <h3 className="action-confirm-title" style={{ marginBottom: '8px' }}>{`Add to ${collectionLabel}`}</h3>
            <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>
              {`Add "${tune?.title || 'Untitled'}" to an existing ${collectionLabelLower}.`}
            </p>
            <input
              className="search-input"
              placeholder={`Search ${isNotesMode ? 'folders' : 'sets'}...`}
              value={addToSetQuery}
              onChange={(e) => setAddToSetQuery(e.target.value)}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '10px' }}
              autoFocus
            />
            {addToSetLoading ? (
              <p style={{ textAlign: 'center', opacity: 0.75, margin: '6px 0 10px' }}>Loading...</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '240px', overflowY: 'auto', marginBottom: '10px' }}>
                {addToSetTargets
                  .filter((set) => !addToSetQuery.trim() || (set.title || '').toLowerCase().includes(addToSetQuery.toLowerCase()))
                  .slice(0, 60)
                  .map((set) => (
                    <button
                      key={set.id}
                      className="btn-subtle search-control-btn"
                      onClick={() => handleAddCurrentItemToSet(set.id)}
                      style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '6px 10px' }}
                    >
                      {set.title || `Untitled ${collectionLabel}`}
                    </button>
                  ))}
              </div>
            )}
            <div className="action-confirm-actions">
              <button
                className="btn-subtle action-confirm-cancel"
                onClick={() => {
                  setShowAddToSetPicker(false);
                  setAddToSetQuery('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <SendExportModal
        open={showSendPicker}
        onClose={() => setShowSendPicker(false)}
        title={tune?.title}
        user={user}
        onSend={handleSendTune}
        type="tune"
        item={tune}
        tunes={[]}
      />

    </div>
    </PageWrapperAuto>
  );
}
